import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { migrateNotes, getContrastTextColor, FONT_STACKS, hexToRgba } from '@/lib/utils';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { MultiSelect } from "@/components/ui/multiselect";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  FileText,
  CheckCircle,
  Edit2,
  Trash2,
  AlertCircle,
  Search,
  Filter,
  MoreVertical,
  Banknote,
  Percent,
  X,
  Save,
  ArrowUpDown,
  Settings2,
  Download,
  Eye,
  Printer,
  PanelLeftOpen,
  PanelLeftClose,
  TrendingDown,
  Undo2,
  Plus,
  Loader2,
} from "lucide-react";
import { format } from "date-fns";
import { cn, toLocalDateString } from "@/lib/utils";
import { ColumnConfiguratorDrawer } from "@/components/column-configurator-drawer";
import type { ColumnDefinition } from "@/components/column-configurator-drawer";
import { DataTableEnhanced, cycleColumnSort } from "@/components/ui/data-table-enhanced";
import type { ColumnDef, ColumnSort } from "@/components/ui/data-table-enhanced";
import { PaginationControls } from "@/components/pagination-controls";
import { calculatePagination, paginateArray } from "@/types/pagination";
import type { PaginationMetadata } from "@/types/pagination";
import type { Client } from "@/types";
import type { Company } from "@/types/company";
import type { InvoiceTemplate } from "@/lib/invoice-template-types";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useInvoiceTemplates } from "@/contexts/InvoiceTemplateContext";
import { getCustomerTerms, getCustomerTaxRate, validateInvoiceNumber, getDataMode, fetchClientFinancials } from "@/lib/data-service";
import type { BillingInvoiceLineItem } from "@/lib/data-service";
import type { DueDateType } from "@/lib/date-utils";
import { calculateDueDate, mapApiDueType } from "@/lib/date-utils";
import { getClientPhone } from "@/lib/client-contact-utils";
import { resolveInvoiceTokens } from "@/services/customerTermsService";
import type { TokenContext } from "@/services/customerTermsService";
import {
  draftWipIds,
  excludeExistingDraftRows,
  toReinstatableRows,
  countSessionAddedFromFeed,
  computeAddLinesBadgeCount,
  toggleRowSelection,
  setPageSelection,
  matchesPickerSearch,
} from "@/lib/add-lines-picker";
import type { WipQueryOptions } from "@/lib/wip-filtering";
import { AgreedValueDialog } from "@/components/billing/AgreedValueDialog";
import type { AgreedValueResult, AgreedValueLineItem } from "@/components/billing/AgreedValueDialog";
import { SendInvoiceStep, type DraftEmailData } from "@/components/billing/SendInvoiceStep";
import { toast } from "sonner";
import { generateInvoicePdf, buildCompanyDetails, blobToBase64, buildPreviewDataForPdf, downloadPdf } from '@/services/invoice-pdf-service';
import { formatClientInvoiceAddress } from '@/lib/client-invoice-address';
import type { InvoicePdfTransaction } from '@/types/invoice-pdf';
import { groupLineItems, resolveGroupLabel, getDescriptionTemplateForGroup, formatLineItemTypeLabel } from "@/lib/invoice-group-utils";
import type { GroupByOption } from "@/lib/invoice-template-types";
import type { InvoiceLineItem as SharedInvoiceLineItem } from "@/lib/invoice-template-types";
import { getBillingInvoiceSettings } from "@/hooks/use-billing-invoice-settings";
import { getCompanyDetails, setCurrentCompanyId } from "@/services/companyService";

// Flat detail transform — used only for Step 1 (always detail view)
function transformToDetailMode(wipItems: WipItem[]): InvoiceLineItem[] {
  return wipItems.map((item) => ({
    id: `line-${item.id}`,
    date: new Date(item.date),
    type: item.type,
    resource: item.resource,
    description: item.description,
    costCentre: item.costCentre || '—',
    reference: item.reference || '—',
    originalValue: item.amount,
    quantity: item.quantity,
    unit: item.unit,
    rate: item.rate,
    costRate: item.costRate || 0,
    amount: item.quantity * item.rate,
    originalWipId: item.id,
    role: item.role,
    isGrouped: false,
  }));
}

// ============================================================================
// TASK 1.10 — Adapter: Wizard InvoiceLineItem → Shared InvoiceLineItem
// ============================================================================
// The wizard uses its own local InvoiceLineItem shape (Date objects, `quantity`,
// `rate`, `resource`, single `costCentre` string). The shared grouping utility
// expects the canonical InvoiceLineItem from invoice-template-types.ts (string
// dates, `qty`, `net`, `person`, split `costCentreCode`/`costCentreName`).
//
// This adapter converts between the two shapes so we can call groupLineItems()
// without modifying either the utility or the wizard's existing data model.
// ============================================================================
function toSharedLineItems(items: InvoiceLineItem[], customerVatRate: number, customerVatCode: string): SharedInvoiceLineItem[] {
  return items.map((item) => ({
    id: item.id,
    date: item.date instanceof Date
      ? item.date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
      : String(item.date),
    description: item.description,
    costCentreCode: item.costCentre !== '—' ? item.costCentre : '',
    costCentreName: item.costCentre !== '—' ? item.costCentre : '',
    person: item.resource || '',
    role: item.role || '',
    phase: '',   // Not present on wizard's WipItem — defaults to empty
    task: '',    // Not tracked by the wizard — defaults to empty
    qty: item.quantity,
    net: item.rate,
    vat: item.amount * (customerVatRate / 100),
    vatCode: customerVatCode,
    amount: item.amount,
    typeLabel: item.type,
  }));
}



interface WipItem {
  id: string;
  date: string;
  resource: string;
  description: string;
  // WIP source transaction type (e.g. 'Time', 'Expense', 'Purchase', 'Invoice',
  // 'Journal', 'VAT Journal'). Kept as the human-readable label so the invoice
  // reflects the actual transaction type rather than collapsing to a fixed set.
  type: string;
  quantity: number;
  unit: string;
  rate: number; // Bill Rate
  amount: number;
  costCentre?: string;
  reference?: string;
  costRate?: number;
  role?: string; // Add role for grouping
}

interface InvoiceLineItem {
  id: string;
  date: Date;
  // WIP source transaction type label (see WipItem.type).
  type: string;
  resource: string;
  description: string;
  costCentre: string;
  reference: string;
  originalValue: number; // Original amount before any edits
  quantity: number;
  unit: string;
  rate: number;
  costRate: number;
  amount: number;
  originalWipId?: string;
  // Grouping metadata
  isGrouped?: boolean;
  groupedItemsCount?: number;
  groupedItemIds?: string[];
  isEdited?: boolean; // true if description, qty, rate, bulk adjustment or agreed value has been changed
  role?: string; // Add role for grouping
}

/**
 * Edit-Draft mode payload.
 *
 * When supplied, the wizard opens pre-populated with the saved draft invoice's
 * line items & values (instead of freshly-selected WIP items). All completion
 * callbacks (`onSaveAsDraft`, `onCreateSalesTransaction`, `onInvoiceGenerated`)
 * receive `editingDraftId` on the invoice object so the parent updates & posts
 * the existing draft rather than creating a new one.
 */
export interface EditDraftData {
  invoiceId: number;
  invoiceDate?: string;
  dueDate?: string;
  description?: string;
  notes?: string;
  invoiceNumber?: string;
  /** Email draft persisted with the invoice, restored on the Send Invoice step. */
  emailDraft?: {
    to?: string;
    cc?: string;
    bcc?: string;
    subject?: string;
    body?: string;
    templateId?: string;
  };
  lineItems: Array<{
    wipLedgerId?: number;
    description: string;
    quantity: number;
    rate: number;
    netAmount: number;
    /** WIP source transaction type label (see WipItem.type). */
    type?: string;
    date?: string;
    resource?: string;
    role?: string;
  }>;
}

interface InvoiceGenerationWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedWipItems: WipItem[];
  /**
   * When present, the wizard runs in "edit draft" mode: Step 1 is seeded with
   * the saved draft's lines/values and the footer exposes a "Return to WIP"
   * action. Absent = normal "generate from unbilled work" mode.
   */
  editDraft?: EditDraftData | null;
  /**
   * Called when the user chooses "Return to WIP" in edit-draft mode.
   * Deletes the draft and returns its WIP items to Unbilled Work.
   */
  onReturnDraftToWip?: (invoiceId: number) => Promise<void>;
  /**
   * All unbilled WIP items available for this project.
   * Provided in edit-draft mode so the user can add additional lines
   * from unbilled work to the existing draft. Items already on the draft
   * are automatically excluded from the picker.
   */
  availableWipItems?: WipItem[];
  /**
   * Edit-draft mode — loads one server page of unbilled WIP for the "Add Lines"
   * picker. When omitted (demo mode, or a parent that cannot page) the picker
   * pages `availableWipItems` client-side instead.
   */
  onLoadUnbilledWipPage?: (
    page: number,
    pageSize: number,
    options?: WipQueryOptions
  ) => Promise<{ items: WipItem[]; totalCount: number }>;
  projectName: string;
  projectCode?: string;  // Project code for external systems
  client?: Client | null;  // Customer information
  company?: Company | null;  // Issuer company information
  onInvoiceGenerated?: (invoice: any) => void;
  vatRate?: number;  // VAT rate as percentage (default 20 for UK)
  // Billing settings
  expenseRecharge?: string;  // 'cost' | 'markup' | 'not-rechargeable'
  expenseMarkupPercent?: string;
  purchaseRecharge?: string;  // 'cost' | 'markup' | 'not-rechargeable'
  purchaseMarkupPercent?: string;
  notToExceedCapEnabled?: boolean;
  notToExceedCapAmount?: string;
  /** Task 2.11 — ID of the invoice template pre-assigned to this project. */
  invoiceTemplateId?: string | number;
  /** Task 2.12 — Called when the user changes the template in Step 2. */
  onTemplateChange?: (templateId: string | number) => void;
  /**
   * ID of the email template pre-assigned to this project for invoices.
   * Passed through to SendInvoiceStep so the send screen pre-selects
   * the correct project-level email template.
   */
  defaultEmailTemplateId?: string | number | null;
  /** Project description — shown in the project section of the invoice preview. */
  projectDescription?: string;
  /** Project manager display name — shown in the project section of the invoice preview. */
  projectManagerName?: string;
  /**
   * Phase 4 (Task 4.1) — Fixed Fee mode.
   * When present, the wizard enters Fixed Fee mode: Step 1 shows a single
   * pre-populated line item (editable description, read-only amount) instead
   * of the full WIP item table. Step 2 is unchanged.
   */
  billingEvent?: { name: string; amount: number; dueDate: string };
  /**
   * Async callback invoked before the emailer MFE is opened.
   * Creates the sales transaction, generates PDF, and stores it.
   * Returns { success, pdfBase64 } so the emailer can attach the PDF.
   */
  onCreateSalesTransaction?: (invoice: any, options?: { finaliseWithoutEmail?: boolean }) => Promise<{ success: boolean; pdfBase64?: string; invoiceNumber?: string }>;
  /** Called after the emailer MFE closes successfully (invoice sent). */
  onSendComplete?: () => void;
  /**
   * Async callback invoked when the user clicks "Save as Draft".
   * Creates a billing draft via the API without posting to financials.
   */
  onSaveAsDraft?: (invoice: any) => Promise<void>;
}

// ============================================================================
// Task 6.7d — WizLineItemsTable
// Extracted from `case 'lineItems'` in tplSectionOrder.map to avoid Babel TSX
// parser failures that occur when complex JSX (TypeScript generics, ??, deep
// nesting) lives inside a switch-case block inside a .map() inside JSX.
// All data is passed as props so the component is fully self-contained.
// ============================================================================

// Type aliases live at module scope to avoid Babel issues with type declarations
// inside function bodies that also contain JSX.
type WizLineItem = InvoiceLineItem;
type WizGroup = NonNullable<ReturnType<typeof groupLineItems>>[number];

// Column definition type for WizLineItemsTable — declared at module scope
// so WizLineItemsTable's function body never needs Record<K, V> generics
// (which Babel TSX can misparse as JSX when inside function bodies).
interface WizColDef {
  show: boolean;
  label: string;
  align: 'left' | 'right';
  cell: (line: WizLineItem) => React.ReactNode;
  summaryCell: (group: WizGroup) => React.ReactNode;
}
type WizColDefs = Record<string, WizColDef>;

// Minimal tplState shape needed by WizLineItemsTable — matches the useMemo object
// in InvoiceGenerationWizard. Using an explicit interface avoids ReturnType hacks.
interface WizTplStateForTable {
  showDate: boolean; showDescription: boolean; showPerson: boolean; showRole: boolean;
  showQty: boolean; showRate: boolean; showVAT: boolean; showVATCode: boolean;
  showAmount: boolean; showCostCentreCode: boolean; showCostCentreName: boolean;
  showType: boolean;
  groupBy: string;
  groupedRowDescription: string; groupedRowDescriptionExpense: string; groupedRowDescriptionPurchase: string;
  primaryColor: string; accentTextColor: string;
  showBorders: boolean;
}

interface WizLineItemsTableProps {
  tplState: WizTplStateForTable;
  tplColumnOrder: string[];
  finalInvoiceLines: InvoiceLineItem[];
  groupDescriptionOverrides: Record<string, string>;
  setGroupDescriptionOverrides: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  vatRate: number;
  vatCode: string;
  rowColorMap: Record<string, string>;
  groupRowColorMap: Record<number, string>;
}

function WizLineItemsTable({
  tplState,
  tplColumnOrder,
  finalInvoiceLines,
  groupDescriptionOverrides,
  setGroupDescriptionOverrides,
  vatRate,
  vatCode,
  rowColorMap,
  groupRowColorMap,
}: WizLineItemsTableProps) {

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
    }).format(amount);
  };

  const wizColDefs: WizColDefs = {
    date:           { show: tplState.showDate,           label: 'Date',             align: 'left',  cell: (l) => format(l.date, "dd/MM/yyyy"),                       summaryCell: () => '' },
    description:    { show: tplState.showDescription,    label: 'Description',      align: 'left',  cell: (l) => l.description,                                      summaryCell: (g) => {
      const tpl = getDescriptionTemplateForGroup(g.items, tplState.groupedRowDescription, tplState.groupedRowDescriptionExpense, tplState.groupedRowDescriptionPurchase);
      const resolved = tpl ? resolveGroupLabel(tpl.replace(/<[^>]*>/g, ''), g.items, { totalQty: g.totalQty, subtotalAmount: g.subtotalAmount, itemCount: g.itemCount }) : '';
      const fallbackValue = resolved || g.groupLabel;
      const inputValue = groupDescriptionOverrides[g.groupKey] !== undefined ? groupDescriptionOverrides[g.groupKey] : fallbackValue;
      const groupKey = g.groupKey;
      // Pre-compute handler — no type annotation to avoid Babel TSX generic parse issues
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const handleChange = (e: any) => {
        setGroupDescriptionOverrides((prev) => {
          const next = { ...prev };
          next[groupKey] = e.target.value;
          return next;
        });
      };
      return (
        <input
          type="text"
          className="w-full bg-transparent border-b border-dashed border-muted-foreground/40 focus:border-primary focus:outline-none text-sm font-medium py-0.5"
          value={inputValue}
          onChange={handleChange}
          title="Click to edit description for this group (this invoice only)"
        />
      );
    }},
    person:         { show: tplState.showPerson,         label: 'Person',           align: 'left',  cell: (l) => l.resource || '—',                                  summaryCell: () => '' },
    role:           { show: tplState.showRole,           label: 'Role',             align: 'left',  cell: (l) => l.role || '—',                                      summaryCell: () => '' },
    qty:            { show: tplState.showQty,            label: 'Qty',              align: 'right', cell: (l) => l.quantity.toFixed(2),                               summaryCell: () => '' },
    net:            { show: tplState.showRate,           label: 'Rate',             align: 'right', cell: (l) => formatCurrency(l.rate),                              summaryCell: () => '' },
    vat:            { show: tplState.showVAT,            label: 'VAT',              align: 'right', cell: (l) => formatCurrency(l.amount * (vatRate / 100)),          summaryCell: () => '' },
    vatCode:        { show: tplState.showVATCode,        label: 'VAT Code',         align: 'left',  cell: () => vatCode,                                               summaryCell: () => '' },
    amount:         { show: tplState.showAmount,         label: 'Amount',           align: 'right', cell: (l) => formatCurrency(l.amount),                           summaryCell: (g) => <span className="font-medium">{formatCurrency(g.subtotalAmount)}</span> },
    costCentreCode: { show: tplState.showCostCentreCode, label: 'Cost Centre Code', align: 'left',  cell: (l) => l.costCentre !== '—' ? l.costCentre : '',           summaryCell: () => '' },
    costCentreName: { show: tplState.showCostCentreName, label: 'Cost Centre Name', align: 'left',  cell: (l) => l.costCentre !== '—' ? l.costCentre : '',           summaryCell: () => '' },
    type:           { show: tplState.showType,            label: 'Type',             align: 'left',  cell: (l) => formatLineItemTypeLabel((l as unknown as { type?: string }).type ?? (l as unknown as { lineItemType?: string }).lineItemType), summaryCell: () => '' },
  };

  const isWizGroupedOut = (f: string) =>
    (tplState.groupBy || 'none') !== 'none' && f !== 'description' && f !== 'amount';

  const wizVisibleCols = tplColumnOrder.filter(f => wizColDefs[f]?.show && !isWizGroupedOut(f));

  const grouped = groupLineItems(
    toSharedLineItems(finalInvoiceLines, vatRate, vatCode),
    (tplState.groupBy || 'none') as GroupByOption,
  );

  return (
    <div className="border rounded-lg overflow-hidden overflow-x-auto">
        <table className={`w-full text-sm table-fixed ${tplState.showBorders ? 'border-collapse' : ''}`}>
          {/* Task 6.5d / 6.7d — accent colour on main invoice thead */}
          <thead style={{ backgroundColor: tplState.primaryColor, color: tplState.accentTextColor }}>
            <tr>
              {wizVisibleCols.map(field => {
                const col = wizColDefs[field];
                return (
                  <th key={field} className={`p-2 whitespace-normal break-words ${col.align === 'right' ? 'text-right' : 'text-left'} ${tplState.showBorders ? 'border border-border' : ''}`}>
                    {col.label}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {grouped === null
              ? finalInvoiceLines.map((line) => (
                  <tr key={line.id} className={tplState.showBorders ? '' : 'border-t'} style={{ backgroundColor: rowColorMap[line.id] }}>
                    {wizVisibleCols.map(field => {
                      const col = wizColDefs[field];
                      return (
                        <td key={field} className={`p-2 ${field === 'date' ? 'whitespace-nowrap' : ''} ${col.align === 'right' ? 'text-right' : ''} ${tplState.showBorders ? 'border border-border' : ''}`}>
                          {col.cell(line)}
                        </td>
                      );
                    })}
                  </tr>
                ))
              : grouped.map((group, gIdx) => (
                  <tr key={`group-summary-${group.groupKey}`} className={tplState.showBorders ? '' : 'border-t'} style={{ backgroundColor: groupRowColorMap[gIdx] }}>
                    {wizVisibleCols.map(field => {
                      const col = wizColDefs[field];
                      return (
                        <td key={field} className={`p-2 ${col.align === 'right' ? 'text-right' : ''} ${tplState.showBorders ? 'border border-border' : ''}`}>
                          {col.summaryCell(group)}
                        </td>
                      );
                    })}
                  </tr>
                ))
            }
          </tbody>
        </table>
    </div>
  );
}

const INVOICE_LINE_DEFAULT_COLUMNS: ColumnDefinition[] = [
  { id: "date", name: "Date", label: "Date", description: "Transaction date", visible: true, locked: false, category: "Timeline", order: 0 },
  { id: "type", name: "Type", label: "Type", description: "Transaction type", visible: true, locked: false, category: "Status", order: 1 },
  { id: "resource", name: "Resource", label: "Resource", description: "Resource name", visible: true, locked: false, category: "Resource", order: 2 },
  { id: "description", name: "Description", label: "Description", description: "Line item description", visible: true, locked: true, category: "Status", order: 3 },
  { id: "costCentre", name: "Cost Centre", label: "Cost Centre", description: "Cost centre code", visible: false, locked: false, category: "Financial", order: 4 },
  { id: "reference", name: "Reference", label: "Reference", description: "Reference number", visible: false, locked: false, category: "Status", order: 5 },
  { id: "originalValue", name: "Original Value", label: "Original Value", description: "Original WIP value", visible: true, locked: false, category: "Financial", order: 6, align: "right" },
  { id: "quantity", name: "Qty", label: "Qty", description: "Quantity", visible: true, locked: false, category: "Financial", order: 7, align: "right" },
  { id: "rate", name: "Rate", label: "Rate", description: "Bill rate", visible: true, locked: false, category: "Financial", order: 8, align: "right" },
  { id: "amount", name: "Amount", label: "Amount", description: "Line total", visible: true, locked: false, category: "Financial", order: 9, align: "right" },
];

export function applyAgreedValueAllocations<T extends {
  id: string;
  amount: number;
  quantity: number;
  rate: number;
}>(
  items: T[],
  result: Pick<AgreedValueResult, "lineIds" | "allocations" | "scaleFactor">
): T[] {
  const allocationByLineId = new Map(result.allocations.map((allocation) => [allocation.lineId, allocation.amount]));

  return items.map((item) => {
    if (!result.lineIds.includes(item.id)) return item;
    const newAmount = allocationByLineId.get(item.id) ?? item.amount * result.scaleFactor;
    const newRate = item.quantity > 0 ? newAmount / item.quantity : item.rate * result.scaleFactor;
    return { ...item, rate: newRate, amount: newAmount, isEdited: true } as T;
  });
}

export function InvoiceGenerationWizard({
  open,
  onOpenChange,
  selectedWipItems,
  editDraft,
  onReturnDraftToWip,
  availableWipItems,
  onLoadUnbilledWipPage,
  projectName,
  projectCode,
  client,
  company,
  onInvoiceGenerated,
  vatRate = 20,  // Default 20% VAT for UK
  expenseRecharge = 'cost',
  expenseMarkupPercent = '0',
  purchaseRecharge = 'cost',
  purchaseMarkupPercent = '0',
  notToExceedCapEnabled = false,
  notToExceedCapAmount = '0',
  invoiceTemplateId,
  onTemplateChange,
  billingEvent,
  projectDescription,
  projectManagerName,
  defaultEmailTemplateId,
  onCreateSalesTransaction,
  onSendComplete,
  onSaveAsDraft,
}: InvoiceGenerationWizardProps) {
  // Phase 4 (Task 4.1) — derived flag for Fixed Fee mode
  const isFixedFeeMode = billingEvent != null;
  // Phase 4 (Task 4.2) — editable description for the Fixed Fee line item
  const [fixedFeeDescription, setFixedFeeDescription] = useState((billingEvent?.name ?? "").slice(0, 30));
  const { templates, getDefaultTemplate } = useInvoiceTemplates();
  const [currentStep, setCurrentStep] = useState(1);
  const [invoiceLineItems, setInvoiceLineItems] = useState<InvoiceLineItem[]>([]); // Step 1: Always detailed view
  const [finalInvoiceLines, setFinalInvoiceLines] = useState<InvoiceLineItem[]>([]); // Step 2+: Grouped based on settings
  // Task 1.16b — per-group description overrides, keyed by group.groupKey; discarded on wizard close
  const [groupDescriptionOverrides, setGroupDescriptionOverrides] = useState<Record<string, string>>({});

  // Edit-Draft — the draft's saved lines as seeded into Step 1. Kept so a saved
  // line the user removes can be offered back in the "Add Lines" picker (the
  // unbilled WIP feed never contains it — it still has Draft status server-side).
  const [originalDraftLines, setOriginalDraftLines] = useState<InvoiceLineItem[]>([]);

  // Edit-Draft — "Add Lines" picker state. The picker pages the unbilled WIP feed
  // itself (server-side when onLoadUnbilledWipPage is supplied), independently of
  // the Unbilled Work table's paging. Selections are held as id → row so rows on
  // pages that are no longer loaded can still be added on confirm.
  const [addLinesOpen, setAddLinesOpen] = useState(false);
  const [addLinesSelected, setAddLinesSelected] = useState<Map<string, WipItem>>(new Map());
  const [addLinesSearch, setAddLinesSearch] = useState('');
  // Debounced copy of the search box — server-paged mode searches the whole feed,
  // so it must not issue a request per keystroke.
  const [addLinesDebouncedSearch, setAddLinesDebouncedSearch] = useState('');
  const [addLinesPage, setAddLinesPage] = useState(1);
  const [addLinesPageSize, setAddLinesPageSize] = useState(25);
  const [addLinesPageItems, setAddLinesPageItems] = useState<WipItem[]>([]);
  const [addLinesServerTotal, setAddLinesServerTotal] = useState(0);
  const [addLinesLoading, setAddLinesLoading] = useState(false);
  const [addLinesError, setAddLinesError] = useState(false);
  // Monotonic request id — page clicks can outrun the API, so only the latest
  // response is allowed to update the list.
  const addLinesRequestRef = useRef(0);

  // Template state
  const [template, setTemplate] = useState<InvoiceTemplate | null>(null);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);

  // Enriched company with full details (address, bank details, etc.)
  // The company prop from CompanyContext comes from the list endpoint which
  // does not include invoicing fields. We fetch the full details here.
  const [enrichedCompany, setEnrichedCompany] = useState<Company | null>(company ?? null);
  useEffect(() => {
    if (!open || !company) {
      setEnrichedCompany(company ?? null);
      return;
    }
    let cancelled = false;
    setCurrentCompanyId(company.id);
    getCompanyDetails().then((details) => {
      if (cancelled) return;
      setEnrichedCompany({
        ...company,
        registeredAddress: details.registeredAddress,
        bankDetails: details.bankDetails,
        vatNumber: details.vatNumber,
        companyRegistrationNumber: details.companyRegistrationNumber,
        phone: details.phone,
        email: details.email,
        website: details.website,
        logoUrl: details.logoUrl ?? company.logoUrl,
      });
    }).catch(() => {
      if (!cancelled) setEnrichedCompany(company);
    });
    return () => { cancelled = true; };
  }, [open, company]);

  // Enrich the client with its Finance System detail (invoiceAddress, VAT number)
  // when the wizard opens. The `/clients` list endpoint (used by the parent page)
  // returns a lightweight record without the structured billing address — that
  // lives in the Financials system and is only exposed via /clients/{id}/financials.
  // Without this, the client address never renders on the invoice or Backing Sheet
  // in live mode. Demo/tour clients already carry invoiceAddress, so we skip the
  // fetch when it is already present.
  const [enrichedClient, setEnrichedClient] = useState<Client | null>(client ?? null);
  useEffect(() => {
    if (!open || !client) {
      setEnrichedClient(client ?? null);
      return;
    }
    if (client.invoiceAddress) {
      setEnrichedClient(client);
      return;
    }
    const companyId = company?.id;
    if (companyId == null) {
      setEnrichedClient(client);
      return;
    }
    let cancelled = false;
    fetchClientFinancials(client.id, companyId).then((details) => {
      if (cancelled) return;
      setEnrichedClient({
        ...client,
        invoiceAddress: details.invoiceAddress ?? client.invoiceAddress,
        vatNumber: details.vatNumber ?? client.vatNumber,
      });
    }).catch(() => {
      if (!cancelled) setEnrichedClient(client);
    });
    return () => { cancelled = true; };
  }, [open, client, company?.id]);

  // Track previous open state so we only reset to step 1 when the wizard first opens,
  // not when unrelated parent state (e.g. invoiceTemplateId) changes and creates new
  // selectedWipItems array references.
  const prevOpenRef = useRef(false);
  
  // Step 1: Inline editing state (combined review & edit)
  const [editingCellId, setEditingCellId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState<string>("");
  const [selectedLineIds, setSelectedLineIds] = useState<string[]>([]);
  const [lineSearchQuery, setLineSearchQuery] = useState("");

  // Feature 5 — Agreed Value Billing
  const [agreedValueDialogOpen, setAgreedValueDialogOpen] = useState(false);
  // Track which line item IDs have had an agreed value applied (for badge display)
  const [agreedValueLineIds, setAgreedValueLineIds] = useState<string[]>([]);
  // Store the write-off reason selected in the AgreedValueDialog
  const [agreedValueWriteOffReason, setAgreedValueWriteOffReason] = useState<string | undefined>();
  const [columnSorts, setColumnSorts] = useState<ColumnSort[]>([{ columnId: "description", direction: "asc" }]);

  // Pagination state for Step 1
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  
  // Column configuration state for Step 1
  const [columnConfigOpen, setColumnConfigOpen] = useState(false);
  const [columns, setColumns] = useState<ColumnDefinition[]>(INVOICE_LINE_DEFAULT_COLUMNS);

  // Filter state for Step 1 (committed/applied values)
  const [isFilterSheetOpen, setIsFilterSheetOpen] = useState(false);
  const [filterDateFrom, setFilterDateFrom] = useState<Date | undefined>(undefined);
  const [filterDateTo, setFilterDateTo] = useState<Date | undefined>(undefined);
  const [filterResourceFilter, setFilterResourceFilter] = useState<string[]>([]);
  const [filterCostCentreFilter, setFilterCostCentreFilter] = useState<string[]>([]);
  const [filterTypeFilter, setFilterTypeFilter] = useState<string[]>([]);

  // Draft filter state (only committed on Apply)
  const [draftDateFrom, setDraftDateFrom] = useState<Date | undefined>(undefined);
  const [draftDateTo, setDraftDateTo] = useState<Date | undefined>(undefined);
  const [draftResourceFilter, setDraftResourceFilter] = useState<string[]>([]);
  const [draftCostCentreFilter, setDraftCostCentreFilter] = useState<string[]>([]);
  const [draftTypeFilter, setDraftTypeFilter] = useState<string[]>([]);

  // Sync draft state when filter sheet opens
  useEffect(() => {
    if (isFilterSheetOpen) {
      setDraftDateFrom(filterDateFrom);
      setDraftDateTo(filterDateTo);
      setDraftResourceFilter(filterResourceFilter);
      setDraftCostCentreFilter(filterCostCentreFilter);
      setDraftTypeFilter(filterTypeFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFilterSheetOpen]);

  // Invoice details (moved to Step 2 - Confirmation)
  const [invoiceDate, setInvoiceDate] = useState<Date>(new Date());
  
  // Phase 3: Additional invoice fields
  const [customerPoNumber, setCustomerPoNumber] = useState<string>("");
  const [projectPeriodFrom, setProjectPeriodFrom] = useState<Date | undefined>(undefined);
  const [projectPeriodTo, setProjectPeriodTo] = useState<Date | undefined>(undefined);
  const [invoiceNotes, setInvoiceNotes] = useState<string>("");

  
  // Step 2: Invoice details side panel toggle
  const [showInvoiceDetails, setShowInvoiceDetails] = useState(true);
  const [isExportingPdf, setIsExportingPdf] = useState(false);

  // ── Feature 9: Invoice Numbering ─────────────────────────────────────────
  const billingInvoiceSettings = getBillingInvoiceSettings();
  const isAutoNumbering = billingInvoiceSettings.autoNumberingEnabled;

  const [manualInvoiceNumber, setManualInvoiceNumber] = useState('');
  const [isDuplicateInvoiceNumber, setIsDuplicateInvoiceNumber] = useState(false);
  const [isValidatingNumber, setIsValidatingNumber] = useState(false);
  const validateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced invoice number validation via API
  useEffect(() => {
    if (isAutoNumbering || manualInvoiceNumber.trim().length === 0) {
      setIsDuplicateInvoiceNumber(false);
      setIsValidatingNumber(false);
      return;
    }

    const mode = getDataMode();
    if (mode === 'demo') {
      // In demo mode, use a static mock list for validation
      const DEMO_USED_NUMBERS = ['INV-2024-001', 'INV-2024-002', 'INV-2024-003', 'INV-2024-004', 'INV-2025-001', 'INV-2025-002', 'INV-2025-003', 'INV-2025-004'];
      setIsDuplicateInvoiceNumber(DEMO_USED_NUMBERS.includes(manualInvoiceNumber.trim()));
      setIsValidatingNumber(false);
      return;
    }

    setIsValidatingNumber(true);
    if (validateTimerRef.current) clearTimeout(validateTimerRef.current);

    validateTimerRef.current = setTimeout(async () => {
      try {
        const result = await validateInvoiceNumber(manualInvoiceNumber.trim());
        setIsDuplicateInvoiceNumber(result.isDuplicate);
      } catch (err) {
        console.warn('[InvoiceWizard] Invoice number validation failed', err);
        setIsDuplicateInvoiceNumber(false);
      } finally {
        setIsValidatingNumber(false);
      }
    }, 400);

    return () => {
      if (validateTimerRef.current) clearTimeout(validateTimerRef.current);
    };
  }, [manualInvoiceNumber, isAutoNumbering]);

  const isInvoiceNumberMissing =
    !isAutoNumbering &&
    manualInvoiceNumber.trim().length === 0;

  // Payment terms from Financials API
  const [paymentTermsDays, setPaymentTermsDays] = useState<number>(30);
  const [paymentTermsDueType, setPaymentTermsDueType] = useState<DueDateType>('days-from-invoice');
  const [paymentTermsDescription, setPaymentTermsDescription] = useState<string>("Net 30 Days");
  const [paymentTermsLoading, setPaymentTermsLoading] = useState(false);

  // Customer VAT code and rate from Financials API
  const [customerVatCode, setCustomerVatCode] = useState<string>('S');
  const [customerVatRate, setCustomerVatRate] = useState<number>(20);

  // Helper function to apply billing settings (markup and recharge rules)
  const applyBillingSettings = useCallback((items: WipItem[]): WipItem[] => {
    return items
      .filter((item) => {
        // Filter out non-rechargeable items
        if (item.type === 'Expense' && expenseRecharge === 'not-rechargeable') {
          return false;
        }
        if (item.type === 'Purchase' && purchaseRecharge === 'not-rechargeable') {
          return false;
        }
        return true;
      })
      .map((item) => {
        // Apply markup to expenses
        if (item.type === 'Expense' && expenseRecharge === 'markup') {
          const markupPercent = parseFloat(expenseMarkupPercent) || 0;
          const markupMultiplier = 1 + (markupPercent / 100);
          return {
            ...item,
            rate: item.rate * markupMultiplier,
            amount: item.amount * markupMultiplier,
          };
        }
        // Apply markup to purchases
        if (item.type === 'Purchase' && purchaseRecharge === 'markup') {
          const markupPercent = parseFloat(purchaseMarkupPercent) || 0;
          const markupMultiplier = 1 + (markupPercent / 100);
          return {
            ...item,
            rate: item.rate * markupMultiplier,
            amount: item.amount * markupMultiplier,
          };
        }
        return item;
      });
  }, [expenseRecharge, expenseMarkupPercent, purchaseRecharge, purchaseMarkupPercent]);

  // Initialize line items from WIP items when wizard first opens (always show detail view in Step 1).
  // We only reset to step 1 on the open transition (false → true) so that switching
  // templates on Step 2 doesn't snap the user back to Step 1.
  useEffect(() => {
    const justOpened = open && !prevOpenRef.current;
    prevOpenRef.current = open;

    if (!justOpened) return;

    if (editDraft) {
      // Edit-Draft mode: seed Step 1 with the saved draft's lines & values.
      // Billing settings (markup/recharge) are intentionally NOT re-applied —
      // the draft's stored values already reflect them.
      const lines: InvoiceLineItem[] = editDraft.lineItems.map((li, idx) => ({
        id: li.wipLedgerId != null ? `line-wip-${li.wipLedgerId}` : `line-draft-${idx}`,
        date: li.date ? new Date(li.date) : new Date(),
        type: li.type ?? 'Time',
        resource: li.resource ?? '',
        description: li.description,
        costCentre: '—',
        reference: '—',
        originalValue: li.netAmount,
        quantity: li.quantity,
        unit: '',
        rate: li.rate,
        costRate: 0,
        amount: li.netAmount,
        originalWipId: li.wipLedgerId != null ? `wip-${li.wipLedgerId}` : undefined,
        isGrouped: false,
        role: li.role ?? '',
      }));
      setInvoiceLineItems(lines);
      setOriginalDraftLines(lines);
      if (editDraft.invoiceDate) setInvoiceDate(new Date(editDraft.invoiceDate));
      setInvoiceNotes(editDraft.notes ?? '');
    } else if (billingEvent) {
      // Phase 4 (Task 4.2) — Fixed Fee mode: create one synthetic line item from the billing event
      const syntheticLineItem: InvoiceLineItem = {
        id: `ff-line-${billingEvent.name}`,
        date: billingEvent.dueDate ? new Date(billingEvent.dueDate) : new Date(),
        type: 'Time',
        resource: '',
        description: billingEvent.name,
        costCentre: '—',
        reference: '—',
        originalValue: billingEvent.amount,
        quantity: 1,
        unit: 'item',
        rate: billingEvent.amount,
        costRate: 0,
        amount: billingEvent.amount,
      };
      setFixedFeeDescription((billingEvent.name ?? "").slice(0, 30));
      setInvoiceLineItems([syntheticLineItem]);
      setCurrentStep(1);
    } else if (selectedWipItems.length > 0) {
      const processedWipItems = applyBillingSettings(selectedWipItems);
      const lineItems = transformToDetailMode(processedWipItems);
      setInvoiceLineItems(lineItems);
    }

    if (!editDraft) setOriginalDraftLines([]);
    setSelectedLineIds([]);
    setLineSearchQuery("");
    setCurrentStep(1);
    // Reset filters
    setFilterDateFrom(undefined);
    setFilterDateTo(undefined);
    setFilterResourceFilter([]);
    setFilterCostCentreFilter([]);
    setFilterTypeFilter([]);
  }, [open, selectedWipItems, applyBillingSettings, billingEvent, editDraft]);

  // Load invoice template when wizard opens
  // Task 2.11: prefer the invoiceTemplateId prop; fall back to the default template.
  // Templates are loaded from the API via InvoiceTemplateContext.
  useEffect(() => {
    if (open) {
      setIsLoadingTemplate(true);
      let resolved: InvoiceTemplate | null = null;
      if (invoiceTemplateId !== undefined && invoiceTemplateId !== null) {
        const numId = typeof invoiceTemplateId === 'string' ? parseInt(invoiceTemplateId, 10) : invoiceTemplateId;
        resolved = templates.find(t => t.id === numId) ?? null;
      }
      if (!resolved) {
        resolved = getDefaultTemplate() ?? null;
      }
      if (resolved) {
        setTemplate(resolved);
        console.log('✅ Invoice template loaded:', resolved.name);
      }
      setIsLoadingTemplate(false);
    }
  }, [open, invoiceTemplateId, templates, getDefaultTemplate]);

  // Fetch customer payment terms from Financials API when wizard opens
  useEffect(() => {
    if (open && client?.code && company?.id) {
      setPaymentTermsLoading(true);
      getCustomerTerms(client.code, company.id)
        .then((result) => {
          if (result.dueDays != null) {
            setPaymentTermsDays(result.dueDays);
            setPaymentTermsDescription(result.description || `Net ${result.dueDays} Days`);
          } else {
            setPaymentTermsDays(30);
            setPaymentTermsDescription("Net 30 Days");
          }
          setPaymentTermsDueType(mapApiDueType(result.dueType));
        })
        .catch((err) => {
          console.warn("Failed to fetch customer payment terms, using default 30 days:", err);
          setPaymentTermsDays(30);
          setPaymentTermsDescription("Net 30 Days");
          setPaymentTermsDueType('days-from-invoice');
        })
        .finally(() => setPaymentTermsLoading(false));
    } else if (open) {
      // No client code or company — reset to default
      setPaymentTermsDays(30);
      setPaymentTermsDueType('days-from-invoice');
      setPaymentTermsDescription("Net 30 Days");
    }
  }, [open, client?.code, company?.id]);

  // Fetch customer VAT code and rate from Financials API when wizard opens
  useEffect(() => {
    if (open && client?.code && company?.id) {
      getCustomerTaxRate(client.code, company.id)
        .then((result) => {
          // No configured tax code in Financials — fall back to standard S/20%
          setCustomerVatCode(result.vatCode ?? 'S');
          setCustomerVatRate(result.vatRate ?? 20);
        })
        .catch((err) => {
          console.warn("Failed to fetch customer tax rate, using default S/20%:", err);
        });
    } else if (open) {
      // No client code or company — reset to defaults
      setCustomerVatCode('S');
      setCustomerVatRate(20);
    }
  }, [open, client?.code, company?.id]);

  // Filter and sort line items
  const filteredAndSortedLines = useMemo(() => {
    let filtered = invoiceLineItems.filter((item) => {
      // Text search filter
      const matchesSearch = lineSearchQuery === "" ||
        item.description.toLowerCase().includes(lineSearchQuery.toLowerCase()) ||
        item.resource.toLowerCase().includes(lineSearchQuery.toLowerCase()) ||
        item.costCentre.toLowerCase().includes(lineSearchQuery.toLowerCase()) ||
        item.reference.toLowerCase().includes(lineSearchQuery.toLowerCase());

      // Advanced filters
      const matchesType = filterTypeFilter.length === 0 || filterTypeFilter.includes(item.type);
      const matchesDateFrom = !filterDateFrom || item.date >= filterDateFrom;
      const matchesDateTo = !filterDateTo || (() => {
        const endOfDay = new Date(filterDateTo);
        endOfDay.setHours(23, 59, 59, 999);
        return item.date <= endOfDay;
      })();
      const matchesResource = filterResourceFilter.length === 0 || filterResourceFilter.includes(item.resource);
      const matchesCostCentre = filterCostCentreFilter.length === 0 || filterCostCentreFilter.includes(item.costCentre);

      return matchesSearch && matchesType && matchesDateFrom && matchesDateTo && matchesResource && matchesCostCentre;
    });

    // Multi-column sort
    if (columnSorts.length > 0) {
      filtered.sort((a, b) => {
        for (const sort of columnSorts) {
          const aValue = (a as unknown as Record<string, unknown>)[sort.columnId];
          const bValue = (b as unknown as Record<string, unknown>)[sort.columnId];
          let cmp = 0;
          if (typeof aValue === "string" && typeof bValue === "string") {
            cmp = aValue.localeCompare(bValue);
          } else {
            cmp = (aValue as number) - (bValue as number);
          }
          if (cmp !== 0) return sort.direction === "asc" ? cmp : -cmp;
        }
        return 0;
      });
    }

    return filtered;
  }, [invoiceLineItems, lineSearchQuery, columnSorts, filterTypeFilter, filterDateFrom, filterDateTo, filterResourceFilter, filterCostCentreFilter]);

  // Visible columns
  const visibleColumns = useMemo(() => columns.filter((col) => col.visible), [columns]);

  // Pagination logic
  const pagination: PaginationMetadata = calculatePagination(filteredAndSortedLines.length, currentPage, itemsPerPage);
  const paginatedLines = paginateArray(filteredAndSortedLines, currentPage, itemsPerPage).data;

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [lineSearchQuery, columnSorts]);

  // Calculate totals
  const totals = useMemo(() => {
    // Use finalInvoiceLines for Step 2+, invoiceLineItems for Step 1
    const linesToCalculate = currentStep >= 2 ? finalInvoiceLines : invoiceLineItems;
    const subtotal = linesToCalculate.reduce((sum, item) => sum + item.amount, 0);
    const tax = 0; // Can be configured later
    const total = subtotal + tax;

    return { subtotal, tax, total };
  }, [invoiceLineItems, finalInvoiceLines, currentStep]);

  // Map the wizard's final (grouped/transformed) lines into the BillingInvoiceLineItem
  // shape the PDF renderer expects, so the exported PDF shows every line — matching
  // the Preview, Print and emailed templates — rather than a single summed line.
  const pdfBillingLineItems = useMemo<BillingInvoiceLineItem[]>(() => {
    const rate = customerVatRate / 100;
    return finalInvoiceLines.map((line, idx) => {
      const net = line.amount;
      const tax = net * rate;
      return {
        id: idx,
        invoiceId: 0,
        sortOrder: idx,
        description: line.description,
        quantity: line.quantity,
        rate: line.rate,
        netAmount: net,
        taxAmount: tax,
        grossAmount: net + tax,
        wipDate: toLocalDateString(line.date),
        wipResourceName: line.resource,
        wipRoleName: line.role,
        type: line.type,
        wipCostCentreCode: line.costCentre !== '—' ? line.costCentre : '',
        wipCostCentreName: line.costCentre !== '—' ? line.costCentre : '',
      };
    });
  }, [finalInvoiceLines, customerVatRate]);

  // Check if invoice exceeds cap
  const exceedsCap = useMemo(() => {
    if (!notToExceedCapEnabled) return false;
    const capAmount = parseFloat(notToExceedCapAmount) || 0;
    return totals.total > capAmount;
  }, [notToExceedCapEnabled, notToExceedCapAmount, totals.total]);

  const capExceededAmount = useMemo(() => {
    if (!exceedsCap) return 0;
    const capAmount = parseFloat(notToExceedCapAmount) || 0;
    return totals.total - capAmount;
  }, [exceedsCap, notToExceedCapAmount, totals.total]);

  // ── PDF attachment state for SendInvoiceStep ──────────────────────────────
  const [invoicePdfBase64, setInvoicePdfBase64] = useState<string | undefined>(undefined);

  // Generate the invoice PDF when the wizard reaches Step 3 (Send Invoice)
  useEffect(() => {
    if (currentStep !== 3) return;
    let cancelled = false;

    const invoiceNum = isAutoNumbering
      ? 'DRAFT'
      : manualInvoiceNumber.trim() || 'DRAFT';

    const dueDate = calculateDueDate(invoiceDate, paymentTermsDueType, paymentTermsDays);
    const transaction: InvoicePdfTransaction = {
      invoiceNumber: invoiceNum,
      date: toLocalDateString(invoiceDate),
      dueDate: toLocalDateString(dueDate),
      status: 'Draft',
      description: projectName,
      amount: totals.total,
      projectName,
      accountName: client?.name ?? '',
      accountAddress: formatClientInvoiceAddress(enrichedClient?.invoiceAddress),
      projectDescription: projectDescription,
      projectManager: projectManagerName,
    };

    const companyDetails = enrichedCompany ? buildCompanyDetails(enrichedCompany) : undefined;
    const previewData = companyDetails
      ? buildPreviewDataForPdf(transaction, companyDetails, pdfBillingLineItems, customerVatCode, customerVatRate)
      : undefined;

    generateInvoicePdf(transaction, companyDetails, pdfBillingLineItems, template ?? undefined, previewData, invoiceNotes || undefined)
      .then(async (result) => {
        if (cancelled) return;
        const b64 = await blobToBase64(result.pdfBlob);
        if (!cancelled) setInvoicePdfBase64(b64);
      })
      .catch((err) => {
        console.warn('[InvoiceWizard] PDF generation for email attachment failed', err);
      });

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  // Handle step navigation
  const handleNext = () => {
    if (currentStep < 2) {
      // When moving from Step 1 to Step 2, transform the detailed lines based on billing settings
      if (currentStep === 1) {
        // TODO [Task 1.10]: Replace with groupLineItems() from invoice-group-utils when Group By fix is implemented.
        // For now, pass the detail lines through unchanged — grouping will be applied in Task 1.10.
        setFinalInvoiceLines([...invoiceLineItems]);
      }
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleClose = () => {
    setCurrentStep(1);
    setInvoiceLineItems([]);
    setFinalInvoiceLines([]);
    setFixedFeeDescription("");
    setEditingCellId(null);
    setEditingValue("");
    setSelectedLineIds([]);
    setAgreedValueLineIds([]);
    setAgreedValueWriteOffReason(undefined);
    setLineSearchQuery("");
    setInvoiceDate(new Date());
    // Reset Phase 3 fields
    setCustomerPoNumber("");
    setProjectPeriodFrom(undefined);
    setProjectPeriodTo(undefined);
    setInvoiceNotes("");

    // Reset Feature 9 — Invoice Numbering
    setManualInvoiceNumber('');
    // Reset PDF attachment
    setInvoicePdfBase64(undefined);
    // Reset filters
    setFilterDateFrom(undefined);
    setFilterDateTo(undefined);
    setFilterResourceFilter([]);
    setFilterCostCentreFilter([]);
    setFilterTypeFilter([]);
    onOpenChange(false);
  };

  // Inline editing handlers
  const handleCellClick = (lineId: string, field: "description" | "quantity" | "rate", currentValue: string | number) => {
    setEditingCellId(`${lineId}-${field}`);
    // For numbers, preserve decimal places
    if (field === "quantity" || field === "rate") {
      setEditingValue(typeof currentValue === "number" ? currentValue.toFixed(2) : String(currentValue));
    } else {
      setEditingValue(String(currentValue));
    }
  };

  const handleCellSave = (lineId: string, field: "description" | "quantity" | "rate") => {
    // Update invoiceLineItems (Step 1)
    setInvoiceLineItems((prev) =>
      prev.map((item) => {
        if (item.id !== lineId) return item;

        if (field === "description") {
          const hasChanged = editingValue !== item.description;
          return { ...item, description: editingValue, isEdited: item.isEdited || hasChanged };
        } else if (field === "quantity") {
          const newQuantity = parseFloat(editingValue) || 0;
          const hasChanged = newQuantity !== item.quantity;
          return { ...item, quantity: newQuantity, amount: newQuantity * item.rate, isEdited: item.isEdited || hasChanged };
        } else if (field === "rate") {
          const newRate = parseFloat(editingValue) || 0;
          const hasChanged = newRate !== item.rate;
          return { ...item, rate: newRate, amount: item.quantity * newRate, isEdited: item.isEdited || hasChanged };
        }
        return item;
      })
    );

    // Also update finalInvoiceLines (Step 2+) if they exist
    setFinalInvoiceLines((prev) =>
      prev.map((item) => {
        if (item.id !== lineId) return item;

        if (field === "description") {
          const hasChanged = editingValue !== item.description;
          return { ...item, description: editingValue, isEdited: item.isEdited || hasChanged };
        } else if (field === "quantity") {
          const newQuantity = parseFloat(editingValue) || 0;
          const hasChanged = newQuantity !== item.quantity;
          return { ...item, quantity: newQuantity, amount: newQuantity * item.rate, isEdited: item.isEdited || hasChanged };
        } else if (field === "rate") {
          const newRate = parseFloat(editingValue) || 0;
          const hasChanged = newRate !== item.rate;
          return { ...item, rate: newRate, amount: item.quantity * newRate, isEdited: item.isEdited || hasChanged };
        }
        return item;
      })
    );

    setEditingCellId(null);
    setEditingValue("");
  };

  const handleCellCancel = () => {
    setEditingCellId(null);
    setEditingValue("");
  };

  const handleCellKeyDown = (e: React.KeyboardEvent, lineId: string, field: "description" | "quantity" | "rate") => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleCellSave(lineId, field);
      
      // Move to next row, same column
      const currentIndex = paginatedLines.findIndex((line) => line.id === lineId);
      if (currentIndex < paginatedLines.length - 1) {
        const nextLine = paginatedLines[currentIndex + 1];
        setTimeout(() => {
          handleCellClick(nextLine.id, field, field === "description" ? nextLine.description : field === "quantity" ? nextLine.quantity : nextLine.rate);
        }, 0);
      }
    } else if (e.key === "Escape") {
      handleCellCancel();
    } else if (e.key === "Tab") {
      e.preventDefault();
      handleCellSave(lineId, field);
      
      // Define the tab order for editable fields
      const editableFields: ("description" | "quantity" | "rate")[] = ["description", "quantity", "rate"];
      const currentFieldIndex = editableFields.indexOf(field);
      
      if (e.shiftKey) {
        // Shift+Tab: Move to previous field
        if (currentFieldIndex > 0) {
          // Previous field in same row
          const prevField = editableFields[currentFieldIndex - 1];
          const currentLine = paginatedLines.find((line) => line.id === lineId);
          if (currentLine) {
            setTimeout(() => {
              handleCellClick(lineId, prevField, prevField === "description" ? currentLine.description : prevField === "quantity" ? currentLine.quantity : currentLine.rate);
            }, 0);
          }
        } else {
          // First field in row, go to last field of previous row
          const currentIndex = paginatedLines.findIndex((line) => line.id === lineId);
          if (currentIndex > 0) {
            const prevLine = paginatedLines[currentIndex - 1];
            const lastField = editableFields[editableFields.length - 1];
            setTimeout(() => {
              handleCellClick(prevLine.id, lastField, lastField === "rate" ? prevLine.rate : prevLine.quantity);
            }, 0);
          }
        }
      } else {
        // Tab: Move to next field
        if (currentFieldIndex < editableFields.length - 1) {
          // Next field in same row
          const nextField = editableFields[currentFieldIndex + 1];
          const currentLine = paginatedLines.find((line) => line.id === lineId);
          if (currentLine) {
            setTimeout(() => {
              handleCellClick(lineId, nextField, nextField === "description" ? currentLine.description : nextField === "quantity" ? currentLine.quantity : currentLine.rate);
            }, 0);
          }
        } else {
          // Last field in row, go to first field of next row
          const currentIndex = paginatedLines.findIndex((line) => line.id === lineId);
          if (currentIndex < paginatedLines.length - 1) {
            const nextLine = paginatedLines[currentIndex + 1];
            const firstField = editableFields[0];
            setTimeout(() => {
              handleCellClick(nextLine.id, firstField, nextLine.description);
            }, 0);
          }
        }
      }
    }
  };

  // Selection handlers
  const handleSelectLine = (lineId: string) => {
    setSelectedLineIds((prev) =>
      prev.includes(lineId) ? prev.filter((id) => id !== lineId) : [...prev, lineId]
    );
  };

  const handleSelectAll = () => {
    if (selectedLineIds.length === filteredAndSortedLines.length) {
      setSelectedLineIds([]);
    } else {
      setSelectedLineIds(filteredAndSortedLines.map((line) => line.id));
    }
  };

  // Bulk actions
  const handleDeleteSelected = () => {
    setInvoiceLineItems((prev) => prev.filter((item) => !selectedLineIds.includes(item.id)));
    setSelectedLineIds([]);
  };

  const handleBulkRateChange = (changeType: "increase" | "decrease", value: number, isPercent: boolean) => {
    setInvoiceLineItems((prev) =>
      prev.map((item) => {
        if (!selectedLineIds.includes(item.id)) return item;

        let newRate = item.rate;
        if (isPercent) {
          newRate = changeType === "increase" 
            ? item.rate * (1 + value / 100)
            : item.rate * (1 - value / 100);
        } else {
          newRate = changeType === "increase" 
            ? item.rate + value
            : item.rate - value;
        }

        return {
          ...item,
          rate: Math.max(0, newRate),
          amount: item.quantity * Math.max(0, newRate),
          isEdited: true,
        };
      })
    );
  };

  // Feature 5 — Agreed Value confirm handler
  const handleAgreedValueConfirm = (result: AgreedValueResult) => {
    setInvoiceLineItems((prev) => applyAgreedValueAllocations(prev, result));
    setFinalInvoiceLines((prev) => applyAgreedValueAllocations(prev, result));
    // Mark these lines as having an agreed value applied
    setAgreedValueLineIds((prev) => [
      ...prev,
      ...result.lineIds.filter((id) => !prev.includes(id)),
    ]);
    // Store the write-off reason from the dialog
    setAgreedValueWriteOffReason(result.writeOffReason);
    // Deselect lines after applying
    setSelectedLineIds([]);
  };

  const handleDeleteLine = (lineId: string) => {
    setInvoiceLineItems((prev) => prev.filter((item) => item.id !== lineId));
  };

  // Edit-Draft — saved draft lines the user has removed in this session. They are
  // absent from the unbilled WIP feed (still Draft server-side), so they are
  // offered back to the picker from the seeded snapshot instead.
  const removedOriginalLines = useMemo(() => {
    if (!editDraft) return [];
    const existingWipIds = new Set(
      invoiceLineItems.map((li) => li.originalWipId).filter(Boolean)
    );
    return originalDraftLines.filter(
      (li) => li.originalWipId && !existingWipIds.has(li.originalWipId)
    );
  }, [editDraft, invoiceLineItems, originalDraftLines]);

  // Edit-Draft — "Add Lines" picker data.
  //
  // Server-paged mode holds a single page of the unbilled feed; local mode (demo,
  // or no loader supplied) pages `availableWipItems` in memory. Either way, items
  // already on the draft are excluded, and the reinstatable lines — which are not
  // in the unbilled feed at all — are offered on page 1 only so they can never be
  // double-counted against the server's page maths.
  const isAddLinesServerPaged = onLoadUnbilledWipPage != null;

  const existingDraftWipIds = useMemo(() => draftWipIds(invoiceLineItems), [invoiceLineItems]);

  // Local (non-server) mode only: every unbilled item not already on the draft.
  const localAvailableToAdd = useMemo(() => {
    if (!editDraft || isAddLinesServerPaged) return [];
    return excludeExistingDraftRows(availableWipItems ?? [], existingDraftWipIds);
  }, [availableWipItems, editDraft, existingDraftWipIds, isAddLinesServerPaged]);

  const addLinesTotalCount = isAddLinesServerPaged ? addLinesServerTotal : localAvailableToAdd.length;

  // Unbilled rows for the current picker page.
  const addLinesUnbilledRows = useMemo(() => {
    if (!editDraft) return [];
    if (isAddLinesServerPaged) {
      return excludeExistingDraftRows(addLinesPageItems, existingDraftWipIds);
    }
    const start = (addLinesPage - 1) * addLinesPageSize;
    return localAvailableToAdd.slice(start, start + addLinesPageSize);
  }, [
    addLinesPage,
    addLinesPageItems,
    addLinesPageSize,
    editDraft,
    existingDraftWipIds,
    isAddLinesServerPaged,
    localAvailableToAdd,
  ]);

  const reinstatableRows = useMemo(
    () => toReinstatableRows(removedOriginalLines, addLinesUnbilledRows),
    [removedOriginalLines, addLinesUnbilledRows]
  );

  // Rows rendered by the picker for the current page, before the search filter.
  const addLinesPageRows = useMemo<WipItem[]>(
    () => (addLinesPage === 1 ? [...reinstatableRows, ...addLinesUnbilledRows] : addLinesUnbilledRows),
    [addLinesPage, reinstatableRows, addLinesUnbilledRows]
  );

  // Badge count — project-wide, not just the loaded page. The server total counts
  // rows added to the draft in this session (still unbilled server-side), so they
  // are subtracted; locally reinstatable lines are added back on.
  const addLinesBadgeCount = computeAddLinesBadgeCount({
    serverTotalCount: addLinesTotalCount,
    sessionAddedFromFeedCount: isAddLinesServerPaged
      ? countSessionAddedFromFeed(invoiceLineItems, originalDraftLines)
      : 0,
    reinstatableCount: reinstatableRows.length,
  });

  // Loads one picker page; stale responses are discarded.
  const loadAddLinesPage = useCallback(async (page: number, pageSize: number, search = '') => {
    if (!onLoadUnbilledWipPage) return;
    const requestId = ++addLinesRequestRef.current;
    setAddLinesLoading(true);
    setAddLinesError(false);
    try {
      const options: WipQueryOptions = search.trim() === '' ? {} : { search: search.trim() };
      const { items, totalCount } = await onLoadUnbilledWipPage(page, pageSize, options);
      if (requestId !== addLinesRequestRef.current) return;
      setAddLinesPageItems(items);
      setAddLinesServerTotal(totalCount);
    } catch (error) {
      if (requestId !== addLinesRequestRef.current) return;
      console.error('Failed to load unbilled work for the Add Lines picker:', error);
      setAddLinesError(true);
    } finally {
      if (requestId === addLinesRequestRef.current) setAddLinesLoading(false);
    }
  }, [onLoadUnbilledWipPage]);

  // Prefetch page 1 when the wizard opens on a draft so the Add Lines badge is
  // accurate before the picker is opened. Page-size changes are handled by their
  // own handler (they must not reset the selection), hence the narrow deps.
  useEffect(() => {
    if (!open || !editDraft || !onLoadUnbilledWipPage) return;
    setAddLinesPage(1);
    setAddLinesSelected(new Map());
    setAddLinesSearch('');
    setAddLinesDebouncedSearch('');
    void loadAddLinesPage(1, addLinesPageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editDraft?.invoiceId, loadAddLinesPage]);

  useEffect(() => {
    const timer = setTimeout(() => setAddLinesDebouncedSearch(addLinesSearch), 300);
    return () => clearTimeout(timer);
  }, [addLinesSearch]);

  // Server-paged mode re-queries the whole unbilled feed from page 1 whenever the
  // search changes, so matches beyond the loaded page are found. The selection is
  // untouched: it is keyed by row id, not by what the current page shows.
  const addLinesSearchFetchedRef = useRef(false);
  useEffect(() => {
    if (!addLinesSearchFetchedRef.current) {
      addLinesSearchFetchedRef.current = true;
      return;
    }
    if (!open || !editDraft || !isAddLinesServerPaged) return;
    setAddLinesPage(1);
    void loadAddLinesPage(1, addLinesPageSize, addLinesDebouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addLinesDebouncedSearch]);

  // Paging keeps the selection and the active search.
  const handleAddLinesPageChange = (page: number) => {
    setAddLinesPage(page);
    void loadAddLinesPage(page, addLinesPageSize, addLinesDebouncedSearch);
  };

  const handleAddLinesPageSizeChange = (pageSize: number) => {
    setAddLinesPageSize(pageSize);
    setAddLinesPage(1);
    void loadAddLinesPage(1, pageSize, addLinesDebouncedSearch);
  };

  // Edit-Draft — append newly-selected WIP lines to the draft's line items.
  // A reinstated saved line is restored from its seeded snapshot so the save
  // delta sees it as untouched (neither a removal nor an addition).
  const handleConfirmAddLines = () => {
    const toAdd = Array.from(addLinesSelected.values());
    if (toAdd.length === 0) return;
    const reinstatedByWipId = new Map(
      removedOriginalLines.map((li) => [li.originalWipId as string, li])
    );
    const newLines = [
      ...transformToDetailMode(toAdd.filter((item) => !reinstatedByWipId.has(item.id))),
      ...toAdd.map((item) => reinstatedByWipId.get(item.id)).filter((li): li is InvoiceLineItem => li != null),
    ];
    setInvoiceLineItems((prev) => [...prev, ...newLines]);
    setAddLinesSelected(new Map());
    setAddLinesSearch('');
    setAddLinesDebouncedSearch('');
    setAddLinesOpen(false);
  };

  // Edit-Draft mode — snapshot of the current Step-1 detail lines (which reflect
  // any inline edits and "Remove Selected" removals). The parent diffs this
  // against the draft's original lines to derive updateBillingDraft edits/removals.
  // Computed from invoiceLineItems (not finalInvoiceLines) so it is unaffected by
  // Step-2 grouping.
  const buildDraftLineState = () =>
    invoiceLineItems.map((li) => {
      const wipLedgerId = li.originalWipId
        ? (() => {
            const n = parseInt(li.originalWipId.replace('wip-', ''), 10);
            return isNaN(n) ? undefined : n;
          })()
        : undefined;
      return {
        wipLedgerId,
        quantity: li.quantity,
        rate: li.rate,
        netAmount: li.amount,
        description: li.description,
        isEdited: li.isEdited === true,
      };
    });

  // Handle invoice generation
  const handleGenerateInvoice = () => {
    const dueDate = calculateDueDate(invoiceDate, paymentTermsDueType, paymentTermsDays);
    const invoice = {
      date: invoiceDate,
      dueDate,
      lineItems: finalInvoiceLines, // Use the grouped/transformed lines
      totals,
      projectName,
      // Feature 9 — Invoice Numbering:
      // When auto-numbering is OFF, the user has supplied a manual invoice number.
      // When auto-numbering is ON, this is undefined (Access Financials assigns it on post).
      // TODO [ENGINEERING]: Pass manualInvoiceNumber to the createBillingDraft / postBillingDraft
      //   API call so Access Financials uses this number when posting the invoice.
      invoiceNumber: isAutoNumbering ? undefined : manualInvoiceNumber.trim() || undefined,
      // Fixed Fee mode: include billing event data and the (potentially edited) description
      // so the parent component can create the invoice via the correct API path.
      ...(isFixedFeeMode && billingEvent ? {
        isFixedFee: true,
        billingEvent,
        fixedFeeDescription,
      } : {}),
      // Feature 5 — Agreed Value Billing: pass write-off metadata
      agreedValueWriteOff: agreedValueLineIds.length > 0 ? {
        lineIds: agreedValueLineIds,
        writeOffReason: agreedValueWriteOffReason,
      } : undefined,
      invoiceNotes: invoiceNotes || undefined,
      // Edit-Draft mode: tell the parent to update & post the existing draft
      // instead of creating a new one.
      editingDraftId: editDraft?.invoiceId,
      draftLineState: editDraft ? buildDraftLineState() : undefined,
    };

    console.log("Generated Invoice:", invoice);
    onInvoiceGenerated?.(invoice);
    handleClose();
  };

  // Build invoice data and call onCreateSalesTransaction before opening the emailer MFE
  const handleBeforeSend = useCallback(async (options?: { finaliseWithoutEmail?: boolean }): Promise<{ success: boolean; pdfBase64?: string; invoiceNumber?: string }> => {
    if (!onCreateSalesTransaction) return { success: true };

    const dueDate = calculateDueDate(invoiceDate, paymentTermsDueType, paymentTermsDays);
    const invoice = {
      date: invoiceDate,
      dueDate,
      lineItems: finalInvoiceLines,
      totals,
      projectName,
      invoiceNumber: isAutoNumbering ? undefined : manualInvoiceNumber.trim() || undefined,
      ...(isFixedFeeMode && billingEvent ? {
        isFixedFee: true,
        billingEvent,
        fixedFeeDescription,
      } : {}),
      agreedValueWriteOff: agreedValueLineIds.length > 0 ? {
        lineIds: agreedValueLineIds,
        writeOffReason: agreedValueWriteOffReason,
      } : undefined,
      invoiceNotes: invoiceNotes || undefined,
      editingDraftId: editDraft?.invoiceId,
      draftLineState: editDraft ? buildDraftLineState() : undefined,
    };

    return onCreateSalesTransaction(invoice, options);
  }, [onCreateSalesTransaction, invoiceDate, paymentTermsDays, paymentTermsDueType, finalInvoiceLines, invoiceLineItems, totals, projectName, isAutoNumbering, manualInvoiceNumber, isFixedFeeMode, billingEvent, fixedFeeDescription, agreedValueLineIds, agreedValueWriteOffReason, invoiceNotes, editDraft]);

  // Handle save as draft
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  // Edit-Draft mode — "Return to WIP" (delete draft, return items to Unbilled Work)
  const [returnToWipDialogOpen, setReturnToWipDialogOpen] = useState(false);
  const [isReturningToWip, setIsReturningToWip] = useState(false);
  const handleReturnToWip = async () => {
    if (!editDraft || !onReturnDraftToWip) return;
    try {
      setIsReturningToWip(true);
      await onReturnDraftToWip(editDraft.invoiceId);
      setReturnToWipDialogOpen(false);
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to return draft to WIP:", error);
    } finally {
      setIsReturningToWip(false);
    }
  };
  const handleSaveAsDraft = async (emailData?: DraftEmailData) => {
    const dueDate = calculateDueDate(invoiceDate, paymentTermsDueType, paymentTermsDays);
    const lineItems = currentStep >= 2 ? finalInvoiceLines : invoiceLineItems;
    const invoice = {
      date: invoiceDate,
      dueDate,
      lineItems,
      totals,
      projectName,
      invoiceNumber: isAutoNumbering ? undefined : manualInvoiceNumber.trim() || undefined,
      ...(isFixedFeeMode && billingEvent ? {
        isFixedFee: true,
        billingEvent,
        fixedFeeDescription,
      } : {}),
      agreedValueWriteOff: agreedValueLineIds.length > 0 ? {
        lineIds: agreedValueLineIds,
        writeOffReason: agreedValueWriteOffReason,
      } : undefined,
      invoiceNotes: invoiceNotes || undefined,
      emailDraft: emailData,
      editingDraftId: editDraft?.invoiceId,
      draftLineState: editDraft ? buildDraftLineState() : undefined,
    };

    if (onSaveAsDraft) {
      try {
        setIsSavingDraft(true);
        await onSaveAsDraft(invoice);
        onOpenChange(false);
      } catch (error) {
        console.error("Failed to save draft:", error);
      } finally {
        setIsSavingDraft(false);
      }
    } else {
      toast.success('Invoice saved as draft.');
      onOpenChange(false);
    }
  };

  // Handle PDF export
  const handleExportPDF = async () => {
    setIsExportingPdf(true);
    try {
      const invoiceNum = isAutoNumbering
        ? 'DRAFT'
        : manualInvoiceNumber.trim() || 'DRAFT';

      const dueDate = calculateDueDate(invoiceDate, paymentTermsDueType, paymentTermsDays);
      const transaction: InvoicePdfTransaction = {
        invoiceNumber: invoiceNum,
        date: toLocalDateString(invoiceDate),
        dueDate: toLocalDateString(dueDate),
        status: 'Draft',
        description: projectName,
        amount: totals.total,
        projectName,
        accountName: client?.name ?? '',
        accountAddress: formatClientInvoiceAddress(enrichedClient?.invoiceAddress),
        projectDescription: projectDescription,
        projectManager: projectManagerName,
      };

      const companyDetails = enrichedCompany ? buildCompanyDetails(enrichedCompany) : undefined;
      const previewData = companyDetails
        ? buildPreviewDataForPdf(transaction, companyDetails, pdfBillingLineItems, customerVatCode, customerVatRate)
        : undefined;

      const result = await generateInvoicePdf(transaction, companyDetails, pdfBillingLineItems, template ?? undefined, previewData, invoiceNotes || undefined);
      const filename = `${invoiceNum.replace(/[^a-zA-Z0-9-_]/g, '_')}.pdf`;
      downloadPdf(result.pdfBlob, filename);
    } catch (err) {
      console.error('Failed to export PDF:', err);
      toast.error('Failed to export invoice PDF. Please try again.');
    } finally {
      setIsExportingPdf(false);
    }
  };



  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
    }).format(amount);
  };

  // ============================================================================
  // Template-driven helpers for Step 2
  // Uses the same extractTemplateState / extractSectionOrder approach as the
  // InvoiceTemplateBuilder so both builder-saved and mock templates render
  // identically.
  // ============================================================================

  // Extract all toggle / layout values from whichever template format is stored
  const tplState = useMemo(() => {
    const d = {
      showCompanyLogo: true,
      logoPosition: 'left' as 'left' | 'center' | 'right',
      companyLogo: (enrichedCompany?.logoUrl || '') as string | undefined,
      showCompanyName: true,
      showCompanyAddress: true,
      showCompanyPhone: true,
      showCompanyVAT: true,
      showCompanyRegistration: true,
      companyPosition: 'left' as 'left' | 'center' | 'right',
      showClientName: true,
      showClientAddress: true,
      showClientContact: true,
      clientPosition: 'left' as 'left' | 'center' | 'right',
      showInvoiceNumber: true,
      showInvoiceDate: true,
      showDueDate: true,
      invoiceDetailsPosition: 'right' as 'left' | 'center' | 'right',
      showProjectCode: true,
      showProjectName: true,
      showProjectReference: true,
      showProjectDescription: true,
      showProjectManager: true,
      projectPosition: 'left' as 'left' | 'center' | 'right',
      groupBy: 'none',
      groupedRowDescription: '',
      groupedRowDescriptionExpense: '',
      groupedRowDescriptionPurchase: '',
      showDate: true,
      showDescription: true,
      showCostCentreCode: true,
      showCostCentreName: true,
      showPerson: true,
      showRole: true,
      showQty: true,
      showRate: true,
      showVAT: true,
      showVATCode: true,
      showAmount: true,
      showType: false,
      showSubtotal: true,
      showTax: true,
      showDiscount: true,
      showTotal: true,
      showPONumber: false,
      showBillingPeriod: false,
      showBankDetails: false,
      showNotes: true,
      showTermsConditions: true,
      footerPosition: 'left' as 'left' | 'center' | 'right',
      notesContent: '' as string,
      /** Raw HTML content of the T&C field, extracted from the template footer config. */
      termsContent: '' as string,
      /** Task 5.4 / 5.6 — show Backing Sheet (available regardless of groupBy) */
      includeBackingSheet: false,
      /** Task 5.8 — independent column visibility for Backing Sheet */
      backingSheetColumns: {} as Partial<Record<string, boolean>>,
      /** Task 5.8 — independent column display order for Backing Sheet */
      backingSheetColumnOrder: ['date', 'description', 'person', 'role', 'costCentreCode', 'costCentreName', 'qty', 'net', 'vat', 'vatCode', 'amount'] as string[],
      /** Task 5.8 — independent page orientation for Backing Sheet */
      backingSheetOrientation: 'landscape' as 'portrait' | 'landscape',
      // Backing Sheet independent header
      bsShowLogo: true as boolean,
      bsShowCompanyName: true as boolean,
      bsShowCompanyAddress: false as boolean,
      bsShowInvoiceNumber: true as boolean,
      bsShowInvoiceDate: true as boolean,
      bsShowClientName: true as boolean,
      bsShowClientAddress: false as boolean,
      // Backing Sheet independent footer
      bsShowBankDetails: false as boolean,
      bsShowNotes: false as boolean,
      bsShowTermsConditions: false as boolean,
      /** Task 6.5d — accent colour from template branding */
      primaryColor: (template?.branding?.primaryColor || '#2563eb') as string,
      /** Task 6.5d — pre-computed contrast text colour for thead (avoids function calls in deep JSX) */
      accentTextColor: getContrastTextColor(template?.branding?.primaryColor || '#2563eb') as string,
      /** Task 6.7d — alternate row shading toggle */
      alternateRowColors: false as boolean,
      /** Task 6.7d — pre-computed alternate row background colour (avoids hexToRgba calls in deep JSX) */
      accentRowColor: '' as string,
      /** Task 6.8d — full grid borders vs horizontal dividers only */
      showBorders: true as boolean,
    };

    if (!template?.sections) return d;

    const findById = (id: string) => template.sections.find(s => s.id === id);
    const findByType = (type: string) => template.sections.find(s => s.type === type);

    // --- Logo ---
    const logoSec = findById('logo');
    if (logoSec?.config) {
      const c = logoSec.config as unknown as Record<string, unknown>;
      if ('showLogo' in c) d.showCompanyLogo = c.showLogo as boolean;
      if ('showCompanyLogo' in c) d.showCompanyLogo = c.showCompanyLogo as boolean;
      if ('logoPosition' in c) d.logoPosition = c.logoPosition as typeof d.logoPosition;
    }

    // --- Company (builder format: id='company') ---
    const companySec = findById('company');
    if (companySec?.config) {
      const c = companySec.config as unknown as Record<string, unknown>;
      if ('showCompanyName' in c) d.showCompanyName = c.showCompanyName as boolean;
      if ('showCompanyAddress' in c) d.showCompanyAddress = c.showCompanyAddress as boolean;
      if ('showPhone' in c) d.showCompanyPhone = c.showPhone as boolean;
      if ('showCompanyPhone' in c) d.showCompanyPhone = c.showCompanyPhone as boolean;
      if ('showVAT' in c) d.showCompanyVAT = c.showVAT as boolean;
      if ('showRegistration' in c) d.showCompanyRegistration = c.showRegistration as boolean;
      if ('position' in c) d.companyPosition = c.position as typeof d.companyPosition;
    }

    // --- Client (builder format: id='client') ---
    const clientSec = findById('client');
    if (clientSec?.config) {
      const c = clientSec.config as unknown as Record<string, unknown>;
      if ('showClientName' in c) d.showClientName = c.showClientName as boolean;
      if ('showClientAddress' in c) d.showClientAddress = c.showClientAddress as boolean;
      if ('showClientContact' in c) d.showClientContact = c.showClientContact as boolean;
      if ('position' in c) d.clientPosition = c.position as typeof d.clientPosition;
    }

    // --- Invoice (builder format: id='invoice') ---
    const invoiceSec = findById('invoice');
    if (invoiceSec?.config) {
      const c = invoiceSec.config as unknown as Record<string, unknown>;
      if ('showInvoiceNumber' in c) d.showInvoiceNumber = c.showInvoiceNumber as boolean;
      if ('showInvoiceDate' in c) d.showInvoiceDate = c.showInvoiceDate as boolean;
      if ('showDueDate' in c) d.showDueDate = c.showDueDate as boolean;
      if ('showPONumber' in c) d.showPONumber = c.showPONumber as boolean;
      if ('position' in c) d.invoiceDetailsPosition = c.position as typeof d.invoiceDetailsPosition;
    }

    // --- Header (mock format: type='header' — combines company/client/invoice) ---
    const headerSec = findByType('header');
    if (headerSec?.config && !companySec && !clientSec && !invoiceSec) {
      const c = headerSec.config as unknown as Record<string, unknown>;
      if ('showCompanyName' in c) d.showCompanyName = c.showCompanyName as boolean;
      if ('showCompanyAddress' in c) d.showCompanyAddress = c.showCompanyAddress as boolean;
      if ('showCompanyPhone' in c) d.showCompanyPhone = c.showCompanyPhone as boolean;
      if ('showVAT' in c) d.showCompanyVAT = c.showVAT as boolean;
      if ('showRegistration' in c) d.showCompanyRegistration = c.showRegistration as boolean;
      if ('showClientName' in c) d.showClientName = c.showClientName as boolean;
      if ('showClientAddress' in c) d.showClientAddress = c.showClientAddress as boolean;
      if ('showClientContact' in c) d.showClientContact = c.showClientContact as boolean;
      if ('showInvoiceNumber' in c) d.showInvoiceNumber = c.showInvoiceNumber as boolean;
      if ('showInvoiceDate' in c) d.showInvoiceDate = c.showInvoiceDate as boolean;
      if ('showDueDate' in c) d.showDueDate = c.showDueDate as boolean;
    }

    // --- Project (builder: id='project') or Summary (mock: type='summary') ---
    const projectSec = findById('project') || findByType('summary');
    if (projectSec?.config) {
      const c = projectSec.config as unknown as Record<string, unknown>;
      if ('showProjectCode' in c) d.showProjectCode = c.showProjectCode as boolean;
      if ('showProjectName' in c) d.showProjectName = c.showProjectName as boolean;
      if ('showProjectReference' in c) d.showProjectReference = c.showProjectReference as boolean;
      if ('showProjectDescription' in c) d.showProjectDescription = c.showProjectDescription as boolean;
      if ('showProjectManager' in c) d.showProjectManager = c.showProjectManager as boolean;
      if ('showBillingPeriod' in c) d.showBillingPeriod = c.showBillingPeriod as boolean;
      if ('position' in c) d.projectPosition = c.position as typeof d.projectPosition;
    }

    // --- Line Items ---
    const lineItemsSec = findById('lineItems') || findByType('line-items');
    if (lineItemsSec?.config) {
      const c = lineItemsSec.config as unknown as Record<string, unknown>;
      if ('groupBy' in c) d.groupBy = c.groupBy as string;
      if ('groupedRowDescription' in c) d.groupedRowDescription = c.groupedRowDescription as string;
      if ('groupedRowDescriptionExpense' in c) d.groupedRowDescriptionExpense = c.groupedRowDescriptionExpense as string;
      if ('groupedRowDescriptionPurchase' in c) d.groupedRowDescriptionPurchase = c.groupedRowDescriptionPurchase as string;
      if ('includeBackingSheet' in c) d.includeBackingSheet = c.includeBackingSheet as boolean;
      // Task 5.8 — extract independent backing sheet config
      if ('backingSheetColumns' in c && c.backingSheetColumns && typeof c.backingSheetColumns === 'object') {
        d.backingSheetColumns = c.backingSheetColumns as Partial<Record<string, boolean>>;
      }
      if ('backingSheetColumnOrder' in c && Array.isArray(c.backingSheetColumnOrder) && (c.backingSheetColumnOrder as unknown[]).length > 0) {
        d.backingSheetColumnOrder = c.backingSheetColumnOrder as string[];
      }
      if ('backingSheetOrientation' in c) d.backingSheetOrientation = c.backingSheetOrientation as 'portrait' | 'landscape';
      // Backing Sheet independent header/footer toggles
      if ('bsShowLogo' in c) d.bsShowLogo = c.bsShowLogo as boolean;
      if ('bsShowCompanyName' in c) d.bsShowCompanyName = c.bsShowCompanyName as boolean;
      if ('bsShowCompanyAddress' in c) d.bsShowCompanyAddress = c.bsShowCompanyAddress as boolean;
      if ('bsShowInvoiceNumber' in c) d.bsShowInvoiceNumber = c.bsShowInvoiceNumber as boolean;
      if ('bsShowInvoiceDate' in c) d.bsShowInvoiceDate = c.bsShowInvoiceDate as boolean;
      if ('bsShowClientName' in c) d.bsShowClientName = c.bsShowClientName as boolean;
      if ('bsShowClientAddress' in c) d.bsShowClientAddress = c.bsShowClientAddress as boolean;
      if ('bsShowBankDetails' in c) d.bsShowBankDetails = c.bsShowBankDetails as boolean;
      if ('bsShowNotes' in c) d.bsShowNotes = c.bsShowNotes as boolean;
      if ('bsShowTermsConditions' in c) d.bsShowTermsConditions = c.bsShowTermsConditions as boolean;
      if ('alternateRowColors' in c) d.alternateRowColors = c.alternateRowColors as boolean; // Task 6.7d
      if ('showBorders' in c) d.showBorders = c.showBorders as boolean; // Task 6.8d
      // Keep pre-computed colour values in sync (computed here so JSX can use simple property access)
      d.accentTextColor = getContrastTextColor(d.primaryColor);
      d.accentRowColor = d.alternateRowColors ? hexToRgba(d.primaryColor, 0.05) : '';
      if ('columns' in c && c.columns) {
        if (Array.isArray(c.columns)) {
          // Mock/builder array format: [{ id, field, visible, ... }]
          // Reset ALL column flags to false first — only columns explicitly
          // present in the array (with visible: true) will be shown.
          d.showDate = false; d.showDescription = false; d.showCostCentreCode = false;
          d.showCostCentreName = false; d.showPerson = false; d.showRole = false;
          d.showQty = false; d.showRate = false; d.showVAT = false;
          d.showVATCode = false; d.showAmount = false;
          type ColEntry = { id?: string; field?: string; visible?: boolean };
          (c.columns as ColEntry[]).forEach((col) => {
            const key = col.field ?? col.id ?? '';
            const show = col.visible !== false; // default visible if missing
            const fieldMap: Record<string, () => void> = {
              date:           () => { d.showDate           = show; },
              description:    () => { d.showDescription    = show; },
              costCentreCode: () => { d.showCostCentreCode = show; },
              costCentreName: () => { d.showCostCentreName = show; },
              person:         () => { d.showPerson         = show; },
              role:           () => { d.showRole           = show; },
              qty:            () => { d.showQty            = show; },
              rate:           () => { d.showRate           = show; },
              net:            () => { d.showRate           = show; },
              vat:            () => { d.showVAT            = show; },
              vatCode:        () => { d.showVATCode        = show; },
              amount:         () => { d.showAmount         = show; },
              type:           () => { d.showType           = show; },
            };
            fieldMap[key]?.();
          });
        } else if (typeof c.columns === 'object') {
          // Legacy object/dictionary format: { date: true, role: false, ... }
          const cols = c.columns as Record<string, boolean>;
          if ('date' in cols) d.showDate = cols.date;
          if ('description' in cols) d.showDescription = cols.description;
          if ('costCentreCode' in cols) d.showCostCentreCode = cols.costCentreCode;
          if ('costCentreName' in cols) d.showCostCentreName = cols.costCentreName;
          if ('person' in cols) d.showPerson = cols.person;
          if ('role' in cols) d.showRole = cols.role;
          if ('qty' in cols) d.showQty = cols.qty;
          if ('rate' in cols) d.showRate = cols.rate;
          if ('vat' in cols) d.showVAT = cols.vat;
          if ('vatCode' in cols) d.showVATCode = cols.vatCode;
          if ('amount' in cols) d.showAmount = cols.amount;
          if ('type' in cols) d.showType = cols.type;
        }
      }
    }

    // --- Totals ---
    const totalsSec = findById('totals') || findByType('totals');
    if (totalsSec?.config) {
      const c = totalsSec.config as unknown as Record<string, unknown>;
      if ('showSubtotal' in c) d.showSubtotal = c.showSubtotal as boolean;
      if ('showTax' in c) d.showTax = c.showTax as boolean;
      if ('showDiscount' in c) d.showDiscount = c.showDiscount as boolean;
      if ('showTotal' in c) d.showTotal = c.showTotal as boolean;
    }

    // --- Footer ---
    const footerSec = findById('footer') || findByType('footer');
    if (footerSec?.config) {
      const c = footerSec.config as unknown as Record<string, unknown>;
      if ('showBankDetails' in c) d.showBankDetails = c.showBankDetails as boolean;
      if ('showNotes' in c) d.showNotes = c.showNotes as boolean;
      if ('showTermsConditions' in c) d.showTermsConditions = c.showTermsConditions as boolean;
      if ('showTermsAndConditions' in c) d.showTermsConditions = c.showTermsAndConditions as boolean;
      if ('notesContent' in c) d.notesContent = c.notesContent as string;
      // Task 4.4 — extract T&C HTML content for token resolution in wizard
      if ('termsAndConditions' in c) d.termsContent = c.termsAndConditions as string;
      if ('termsContent' in c) d.termsContent = c.termsContent as string;
      if ('notes' in c && !('notesContent' in c)) d.notesContent = c.notes as string;
      if ('position' in c) d.footerPosition = c.position as typeof d.footerPosition;
    }

    // Override logo from branding
    if (template.branding?.logoPosition) {
      d.logoPosition = template.branding.logoPosition;
    }
    if (template.branding?.logo !== undefined) {
      d.companyLogo = template.branding.logo;
    } else if (enrichedCompany?.logoUrl) {
      d.companyLogo = enrichedCompany.logoUrl;
    }

    // Override groupBy from settings
    if (template.settings?.groupBy) {
      d.groupBy = template.settings.groupBy;
    }

    // Fixed Fee mode: force groupBy to 'none' so the single synthetic line item
    // renders with its description directly instead of being grouped under "(Unassigned)".
    if (isFixedFeeMode) {
      d.groupBy = 'none';
    }

    return d;
  }, [template, isFixedFeeMode, enrichedCompany?.logoUrl]);

  // Task 2.4 — orientation from template settings (default portrait)
  const tplOrientation = useMemo(() => {
    return template?.settings?.orientation || 'portrait';
  }, [template]);

  // Task 6.6d / 6.5d — pre-compute A4 paper style to keep JSX attribute expressions simple
  const tplPaperStyle = useMemo(() => ({
    width: tplOrientation === 'landscape' ? '297mm' : '210mm',
    minHeight: tplOrientation === 'landscape' ? '210mm' : '297mm',
    fontFamily: FONT_STACKS[template?.branding?.fontFamily || 'inter'] ?? template?.branding?.fontFamily ?? FONT_STACKS.inter,
    fontSize: ((template?.branding?.fontSize as number | undefined) || 14) + 'px',
    color: template?.branding?.textColor || '#1e293b',
  }), [tplOrientation, template]);

  // Task 2.9 — column order from template line-items section config
  const WIZARD_DEFAULT_COLUMN_ORDER = [
    'date', 'description', 'person', 'role', 'qty', 'net', 'vat', 'vatCode', 'amount',
    'costCentreCode', 'costCentreName',
  ];
  const tplColumnOrder = useMemo(() => {
    const lineItemsSec = template?.sections?.find(s => s.id === 'lineItems' || s.type === 'line-items');
    if (lineItemsSec?.config) {
      const c = lineItemsSec.config as unknown as Record<string, unknown>;
      if (Array.isArray(c.columnOrder) && c.columnOrder.length > 0) {
        return c.columnOrder as string[];
      }
    }
    return WIZARD_DEFAULT_COLUMN_ORDER;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template]);

  // Extract section ordering — supports both builder-format (logo, company, client…)
  // and mock-format (header, line-items, totals, footer) section IDs.
  type PreviewSectionId = 'logo' | 'company' | 'client' | 'invoice' | 'project' | 'lineItems' | 'totals' | 'footer';
  const tplSectionOrder = useMemo((): PreviewSectionId[] => {
    const defaultOrder: PreviewSectionId[] = ['logo', 'company', 'client', 'invoice', 'project', 'lineItems', 'totals', 'footer'];
    if (!template?.sections) return defaultOrder;

    const builderIds = new Set<string>(['logo', 'company', 'client', 'invoice', 'project', 'lineItems', 'totals', 'footer']);
    const hasBuilderFormat = template.sections.some(s => builderIds.has(s.id));

    if (hasBuilderFormat) {
      const sectionMap = new Map(template.sections.map(s => [s.id, s.order]));
      return [...defaultOrder].sort((a, b) => (sectionMap.get(a) ?? defaultOrder.indexOf(a)) - (sectionMap.get(b) ?? defaultOrder.indexOf(b)));
    }

    // Mock format — map type-based sections to builder IDs
    // The mock format has: header (= company+client+invoice), summary (=project), line-items, totals, footer
    // We render them in their order but expand header into company→client→invoice
    return defaultOrder;
  }, [template]);

  // Get the display value for a line item field based on template column field name
  const getLineItemFieldValue = (line: InvoiceLineItem, field: string): string => {
    switch (field) {
      case 'date': return format(line.date, "dd/MM/yyyy");
      case 'description': return line.description;
      case 'person': return line.resource || '—';
      case 'role': return line.role || '—';
      case 'qty':
      case 'hours': return line.quantity.toFixed(2);
      case 'rate':
      case 'net': return formatCurrency(line.rate);
      case 'amount': return formatCurrency(line.amount);
      case 'costCentreCode':
      case 'costCentreName': return line.costCentre || '—';
      case 'projectCode': return projectCode || '—';
      case 'projectName': return projectName || '—';
      case 'vat': return formatCurrency(line.amount * (customerVatRate / 100));
      case 'vatCode': return customerVatCode;
      default: return '—';
    }
  };

  // Helper function to render cell content based on column ID
  const renderCell = (line: InvoiceLineItem, columnId: string) => {
    switch (columnId) {
      case "date":
        return <TableCell className="text-sm">{format(line.date, "dd/MM/yyyy")}</TableCell>;
      
      case "type":
        return (
          <TableCell>
            <Badge variant={line.type === 'Time' ? 'default' : line.type === 'Expense' ? 'secondary' : 'outline'}>
              {line.type}
            </Badge>
          </TableCell>
        );
      
      case "resource":
        return <TableCell className="text-sm">{line.resource}</TableCell>;
      
      case "description":
        return (
          <TableCell
            className="cursor-text hover:bg-muted/50 transition-colors"
            onClick={() => editingCellId !== `${line.id}-description` && handleCellClick(line.id, "description", line.description)}
          >
            {editingCellId === `${line.id}-description` ? (
              <Textarea
                value={editingValue}
                onChange={(e) => setEditingValue(e.target.value)}
                onBlur={() => handleCellSave(line.id, "description")}
                onKeyDown={(e) => handleCellKeyDown(e, line.id, "description")}
                className="min-h-[60px] w-full"
                autoFocus
              />
            ) : (
              <div className="flex items-center gap-2">
                <span className="flex-1 text-sm">{line.description}</span>
                {agreedValueLineIds.includes(line.id) && (
                  <Badge variant="outline" className="text-xs border-purple-300 text-purple-700 dark:border-purple-600 dark:text-purple-400 shrink-0">
                    Agreed
                  </Badge>
                )}
                <Edit2 className="h-3 w-3 opacity-0 group-hover:opacity-50 transition-opacity" />
              </div>
            )}
          </TableCell>
        );
      
      case "costCentre":
        return <TableCell className="text-sm">{line.costCentre}</TableCell>;
      
      case "reference":
        return <TableCell className="text-sm">{line.reference}</TableCell>;
      
      case "originalValue":
        return (
          <TableCell className="text-right text-sm text-muted-foreground">
            {formatCurrency(line.originalValue)}
          </TableCell>
        );
      
      case "quantity":
        return (
          <TableCell
            className="text-right cursor-text hover:bg-muted/50 transition-colors"
            onClick={() => editingCellId !== `${line.id}-quantity` && handleCellClick(line.id, "quantity", line.quantity)}
          >
            {editingCellId === `${line.id}-quantity` ? (
              <Input
                type="number"
                step="0.01"
                value={editingValue}
                onChange={(e) => setEditingValue(e.target.value)}
                onBlur={() => handleCellSave(line.id, "quantity")}
                onKeyDown={(e) => handleCellKeyDown(e, line.id, "quantity")}
                onFocus={(e) => e.target.select()}
                className="text-right [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                autoFocus
              />
            ) : (
              <div className="flex items-center justify-end gap-2">
                <span className="text-sm">{line.quantity.toFixed(2)}</span>
                <Edit2 className="h-3 w-3 opacity-0 group-hover:opacity-50 transition-opacity" />
              </div>
            )}
          </TableCell>
        );
      
      case "rate":
        return (
          <TableCell
            className="text-right cursor-text hover:bg-muted/50 transition-colors"
            onClick={() => editingCellId !== `${line.id}-rate` && handleCellClick(line.id, "rate", line.rate)}
          >
            {editingCellId === `${line.id}-rate` ? (
              <Input
                type="number"
                step="0.01"
                value={editingValue}
                onChange={(e) => setEditingValue(e.target.value)}
                onBlur={() => handleCellSave(line.id, "rate")}
                onKeyDown={(e) => handleCellKeyDown(e, line.id, "rate")}
                onFocus={(e) => e.target.select()}
                className="text-right [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                autoFocus
              />
            ) : (
              <div className="flex items-center justify-end gap-2">
                <span className="text-sm">{formatCurrency(line.rate)}</span>
                <Edit2 className="h-3 w-3 opacity-0 group-hover:opacity-50 transition-opacity" />
              </div>
            )}
          </TableCell>
        );
      
      case "amount":
        return (
          <TableCell className="text-right text-sm font-medium">
            {formatCurrency(line.amount)}
          </TableCell>
        );
      
      default:
        return <TableCell></TableCell>;
    }
  };

  if (!open) return null;

  // Task 5.4 — Pre-compute Backing Sheet node (avoids type declarations inside JSX / Babel parse error)
  const wizBsColDefs: Record<string, { show: boolean; label: string; align: 'left' | 'right'; cell: (item: typeof finalInvoiceLines[number]) => React.ReactNode }> = {
    date:           { show: tplState.showDate,           label: 'Date',             align: 'left',  cell: (i) => format(i.date, 'dd/MM/yyyy') },
    description:    { show: tplState.showDescription,    label: 'Description',      align: 'left',  cell: (i) => i.description },
    person:         { show: tplState.showPerson,         label: 'Person',           align: 'left',  cell: (i) => i.resource || '—' },
    role:           { show: tplState.showRole,           label: 'Role',             align: 'left',  cell: (i) => i.role || '—' },
    qty:            { show: tplState.showQty,            label: 'Qty',              align: 'right', cell: (i) => i.quantity.toFixed(2) },
    net:            { show: tplState.showRate,           label: 'Rate',             align: 'right', cell: (i) => formatCurrency(i.rate) },
    vat:            { show: tplState.showVAT,            label: 'VAT',              align: 'right', cell: (i) => formatCurrency(i.amount * (customerVatRate / 100)) },
    vatCode:        { show: tplState.showVATCode,        label: 'VAT Code',         align: 'left',  cell: () => customerVatCode },
    amount:         { show: tplState.showAmount,         label: 'Amount',           align: 'right', cell: (i) => formatCurrency(i.amount) },
    costCentreCode: { show: tplState.showCostCentreCode, label: 'Cost Centre Code', align: 'left',  cell: (i) => i.costCentre !== '—' ? i.costCentre : '' },
    costCentreName: { show: tplState.showCostCentreName, label: 'Cost Centre Name', align: 'left',  cell: (i) => i.costCentre !== '—' ? i.costCentre : '' },
    type:           { show: tplState.showType,            label: 'Type',             align: 'left',  cell: (i) => formatLineItemTypeLabel((i as unknown as { type?: string }).type ?? (i as unknown as { lineItemType?: string }).lineItemType) },
  };
  // Task 5.8 — use independent backingSheetColumns visibility; fall back to all-visible when empty
  const wizBsColVisible = (field: string): boolean => {
    if (Object.keys(tplState.backingSheetColumns).length === 0) return true;
    return tplState.backingSheetColumns[field] !== false;
  };
  // Task 5.8 — use backingSheetColumnOrder (independent from main invoice column order)
  const wizBsCols = tplState.backingSheetColumnOrder.filter(f => wizBsColDefs[f] && wizBsColVisible(f));

  // Task 6.7d — pre-compute row colour maps: lineItemId → background colour string (or '' for no colour)
  // Using Record<id, string> so JSX can use plain property access rowColorMap[id] with no subscript
  const wizRowColorMap: Record<string, string> = {};
  invoiceLineItems.forEach((l, i) => { wizRowColorMap[l.id] = i % 2 !== 0 ? tplState.accentRowColor : ''; });
  const wizBsRowColorMap: Record<number, string> = {};
  invoiceLineItems.forEach((_l, i) => { wizBsRowColorMap[i] = i % 2 !== 0 ? tplState.accentRowColor : ''; });
  // Task 6.5e — backing sheet thead style using pre-computed accentTextColor from tplState
  const wizBsTheadStyle = {
    backgroundColor: tplState.primaryColor,
    color: tplState.accentTextColor,
  };

  const wizBackingSheetNode = tplState.includeBackingSheet ? (
    <>
      {/* Page break divider */}
      <div className="flex items-center gap-3 px-2 mt-4">
        <div className="flex-1 border-t border-dashed border-slate-400" />
        <span className="text-xs text-slate-400 font-medium tracking-wide uppercase whitespace-nowrap">
          — Backing Sheet —
        </span>
        <div className="flex-1 border-t border-dashed border-slate-400" />
      </div>

      {/* Backing Sheet A4 page */}
      <div className="flex justify-center mt-4">
        <div
          className="bg-white p-8 rounded-lg shadow-sm border mx-auto origin-top"
          style={{
            width: tplState.backingSheetOrientation === 'landscape' ? '297mm' : '210mm',
            minHeight: tplState.backingSheetOrientation === 'landscape' ? '210mm' : '297mm',
            fontFamily: FONT_STACKS[template?.branding?.fontFamily || 'inter'] || template?.branding?.fontFamily || FONT_STACKS.inter, // Task 6.6d
            fontSize: (template?.branding?.fontSize || 14) + 'px',
            color: template?.branding?.textColor || '#1e293b',
          }}
        >
          <div className="space-y-6">
            {/* Independent 3-column header grid using bsShow* toggles */}
            <div className="grid grid-cols-3 gap-4 items-start border-b pb-4">
              {/* Left: Logo + Company */}
              <div className="space-y-1 min-w-0">
                {tplState.bsShowLogo && (tplState.companyLogo || enrichedCompany?.logoUrl) && (
                  <img src={tplState.companyLogo || enrichedCompany?.logoUrl} alt="Logo" className="h-12 w-auto max-w-[120px] object-contain mb-1" />
                )}
                {tplState.bsShowCompanyName && enrichedCompany && (
                  <div className="font-semibold text-sm">{enrichedCompany.name}</div>
                )}
                {tplState.bsShowCompanyAddress && enrichedCompany?.registeredAddress && (
                  <div className="text-xs text-muted-foreground">
                    {enrichedCompany.registeredAddress.line1}
                    {enrichedCompany.registeredAddress.line2 && `, ${enrichedCompany.registeredAddress.line2}`}
                    , {enrichedCompany.registeredAddress.city}
                    {enrichedCompany.registeredAddress.county && `, ${enrichedCompany.registeredAddress.county}`}
                    , {enrichedCompany.registeredAddress.postcode}
                  </div>
                )}
              </div>
              {/* Centre: Backing Sheet label + Invoice ref */}
              <div className="text-center space-y-1 min-w-0">
                <div className="font-semibold text-base">Invoice — Backing Sheet</div>
                {tplState.bsShowInvoiceNumber && <div className="text-sm text-muted-foreground">INV-DRAFT</div>}
                {tplState.bsShowInvoiceDate && invoiceDate && (
                  <div className="text-sm text-muted-foreground">Date: {format(invoiceDate, 'dd/MM/yyyy')}</div>
                )}
              </div>
              {/* Right: Client */}
              <div className="text-right space-y-1 min-w-0">
                {(tplState.bsShowClientName || tplState.bsShowClientAddress) && (
                  <div className="text-xs text-muted-foreground font-medium">Bill To:</div>
                )}
                {tplState.bsShowClientName && enrichedClient && (
                  <div className="font-medium text-sm">{enrichedClient.name}</div>
                )}
                {tplState.bsShowClientAddress && enrichedClient?.invoiceAddress && (
                  <div className="text-xs text-muted-foreground">
                    {formatClientInvoiceAddress(enrichedClient.invoiceAddress)}
                  </div>
                )}
              </div>
            </div>

            {/* Full flat detail table */}
            <div className="border rounded-lg overflow-hidden overflow-x-auto">
              <table className={`w-full text-sm ${tplState.showBorders ? 'border-collapse' : ''}`}>
                {/* Task 6.5e — accent colour on backing sheet thead */}
                <thead style={wizBsTheadStyle}>
                  <tr>
                    {wizBsCols.map(field => {
                      const col = wizBsColDefs[field];
                      return (
                        <th key={field} className={`px-3 py-2 text-xs font-semibold whitespace-nowrap ${col.align === 'right' ? 'text-right' : 'text-left'} ${tplState.showBorders ? 'border border-border' : 'border-b'}`}>
                          {col.label}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {invoiceLineItems.map((item, idx) => (
                    <tr key={idx} style={{ backgroundColor: wizBsRowColorMap[idx] }}>
                      {wizBsCols.map(field => {
                        const col = wizBsColDefs[field];
                        return (
                          <td key={field} className={`px-3 py-2 text-sm ${field === 'date' ? 'whitespace-nowrap' : ''} ${col.align === 'right' ? 'text-right' : ''} ${tplState.showBorders ? 'border border-border' : 'border-b'}`}>
                            {col.cell(item)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Independent BS footer */}
            {(tplState.bsShowBankDetails || tplState.bsShowNotes || tplState.bsShowTermsConditions) && (
              <div className="border-t pt-4 space-y-3 text-sm text-muted-foreground">
                {tplState.bsShowBankDetails && (
                  <div>
                    <div className="font-medium text-foreground text-xs">Bank Details:</div>
                    {enrichedCompany?.bankDetails ? (
                      <div className="text-xs space-y-0.5">
                        {enrichedCompany.bankDetails.bankName && <div>{enrichedCompany.bankDetails.bankName}</div>}
                        {enrichedCompany.bankDetails.accountName && <div>Account Name: {enrichedCompany.bankDetails.accountName}</div>}
                        {enrichedCompany.bankDetails.sortCode && <div>Sort Code: {enrichedCompany.bankDetails.sortCode}</div>}
                        {enrichedCompany.bankDetails.accountNumber && <div>Account No: {enrichedCompany.bankDetails.accountNumber}</div>}
                        {enrichedCompany.bankDetails.iban && <div>IBAN: {enrichedCompany.bankDetails.iban}</div>}
                        {enrichedCompany.bankDetails.swiftBic && <div>SWIFT/BIC: {enrichedCompany.bankDetails.swiftBic}</div>}
                      </div>
                    ) : (
                      <div className="text-xs text-muted-foreground/60 italic">No bank details set</div>
                    )}
                  </div>
                )}
                {tplState.bsShowNotes && (
                  <div>
                    <div className="font-medium text-foreground text-xs">Notes:</div>
                    {(() => {
                      // Mirror the main invoice footer: combine the template notes
                      // with the per-invoice notes typed in this wizard so both
                      // surface on the Backing Sheet.
                      const combined = [migrateNotes(tplState.notesContent), invoiceNotes].filter(Boolean).join('\n\n');
                      return combined
                        ? <div className="prose prose-xs max-w-none text-xs" dangerouslySetInnerHTML={{ __html: combined }} />
                        : <span className="text-xs text-muted-foreground/50">No notes</span>;
                    })()}
                  </div>
                )}
                {tplState.bsShowTermsConditions && (
                  <div>
                    <div className="font-medium text-foreground text-xs">Terms &amp; Conditions:</div>
                    {(() => {
                      // Resolve merge tokens the same way the main invoice footer does.
                      const tcDueDate = calculateDueDate(invoiceDate, paymentTermsDueType, paymentTermsDays);
                      const tcContext: TokenContext = {
                        client: {
                          name: enrichedClient?.name,
                          paymentTermsDays,
                          paymentTermsDescription,
                          paymentTermsCode: paymentTermsDescription
                            ? paymentTermsDescription.replace(/\s+/g, '').toUpperCase()
                            : undefined,
                        },
                        invoice: {
                          date: format(invoiceDate, 'dd/MM/yyyy'),
                          dueDate: format(tcDueDate, 'dd/MM/yyyy'),
                        },
                        project: { name: projectName },
                        company: { name: enrichedCompany?.name },
                      };
                      const resolvedTc = tplState.termsContent
                        ? resolveInvoiceTokens(tplState.termsContent, tcContext)
                        : null;
                      return resolvedTc
                        ? <div className="prose prose-xs max-w-none text-xs [&_.merge-chip]:inline-block [&_.merge-chip]:bg-primary/10 [&_.merge-chip]:text-primary [&_.merge-chip]:border [&_.merge-chip]:border-primary/30 [&_.merge-chip]:rounded [&_.merge-chip]:px-1 [&_.merge-chip]:text-xs [&_.merge-chip]:font-medium" dangerouslySetInnerHTML={{ __html: resolvedTc }} />
                        : <span className="text-xs italic text-muted-foreground/60">No terms and conditions entered.</span>;
                    })()}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  ) : null;

  return (
    <TooltipProvider>
    <>
    {/* ── Print styles ────────────────────────────────────────────────────── */}
    {/* Hides the wizard chrome so only the A4 invoice paper prints.
        Mirrors the approach used in InvoiceViewerOverlay. */}
    <style>{`
      @media print {
        body * { visibility: hidden; }
        #invoice-generation-wizard .wizard-preview-canvas,
        #invoice-generation-wizard .wizard-preview-canvas * { visibility: visible; }
        #invoice-generation-wizard { position: static !important; }
        #invoice-generation-wizard .wizard-preview-toolbar { display: none !important; }
        #invoice-generation-wizard .wizard-preview-canvas {
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
          overflow: visible !important;
          height: auto !important;
          padding: 0 !important;
          background: white !important;
        }
        #invoice-generation-wizard .wizard-preview-canvas .wizard-paper-bg {
          background: none !important;
          padding: 0 !important;
          border-radius: 0 !important;
        }
        #invoice-generation-wizard .wizard-preview-canvas .wizard-paper {
          box-shadow: none !important;
          border: none !important;
          border-radius: 0 !important;
          width: 100% !important;
          max-width: none !important;
          padding: 0 !important;
        }
      }
    `}</style>
    <div id="invoice-generation-wizard" className="fixed inset-0 z-50 bg-background flex flex-col">
      {/* Header Bar */}
      <div className="wizard-chrome border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold">
                {editDraft ? 'Edit Draft Invoice' : 'Generate Invoice'}
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                {editDraft
                  ? `Edit draft ${editDraft.invoiceNumber ?? `#${editDraft.invoiceId}`} • ${projectName}`
                  : isFixedFeeMode
                    ? `Create invoice for ${billingEvent!.name} • ${projectName}`
                    : `Create a new invoice from selected unbilled work items • ${projectName}`}
              </p>
            </div>
            <Button variant="ghost" size="icon" onClick={handleClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-between max-w-2xl">
            {[1, 2, 3].map((step) => (
              <div key={step} className="flex items-center flex-1">
                <div
                  className={cn(
                    "flex items-center justify-center w-10 h-10 rounded-full border-2 transition-colors",
                    currentStep >= step
                      ? "bg-primary border-primary text-primary-foreground"
                      : "border-muted-foreground text-muted-foreground"
                  )}
                >
                  {currentStep > step ? <CheckCircle className="h-5 w-5" /> : step}
                </div>
                <div className="flex-1 px-3">
                  <div
                    className={cn(
                      "text-sm font-medium",
                      currentStep >= step ? "text-foreground" : "text-muted-foreground"
                    )}
                  >
                    {step === 1 && "Review & Edit Lines"}
                    {step === 2 && "Preview Invoice"}
                    {step === 3 && "Send Invoice"}
                  </div>
                </div>
                {step < 3 && (
                  <div
                    className={cn(
                      "h-0.5 flex-1",
                      currentStep > step ? "bg-primary" : "bg-muted"
                    )}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className={`wizard-content flex-1 ${currentStep === 2 ? 'min-h-0' : 'overflow-y-auto'}`}>
        <div className={currentStep === 2 ? 'h-full' : 'container mx-auto px-6 py-6'}>
            {/* Step 3: Send Invoice */}
            {currentStep === 3 && (
              <SendInvoiceStep
                invoiceNumber={isAutoNumbering ? 'DRAFT' : manualInvoiceNumber.trim() || 'DRAFT'}
                clientEmail={client?.contactEmail ?? ''}
                contactName={client?.contactName ?? client?.name ?? ''}
                contactFirstName={client?.contactFirstName ?? ''}
                clientName={client?.name ?? ''}
                clientPhone={getClientPhone(client) ?? ''}
                clientAccountNumber={client?.code ?? ''}
                clientPaymentTermsDays={String(paymentTermsDays)}
                projectName={projectName}
                projectCode={projectCode ?? ''}
                invoiceTotal={new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(totals.subtotal * (1 + customerVatRate / 100))}
                invoiceDate={format(invoiceDate, 'dd MMM yyyy')}
                dueDate={format(calculateDueDate(invoiceDate, paymentTermsDueType, paymentTermsDays), 'dd MMM yyyy')}
                companyName={enrichedCompany?.name ?? ''}
                senderName={enrichedCompany?.name ?? ''}
                senderEmail={enrichedCompany?.email ?? ''}
                defaultEmailTemplateId={defaultEmailTemplateId}
                initialEmailDraft={editDraft?.emailDraft}
                invoicePdfBase64={invoicePdfBase64}
                onBack={() => setCurrentStep(2)}
                onSaveAsDraft={(emailData) => {
                  handleSaveAsDraft(emailData);
                }}
                onBeforeSend={onCreateSalesTransaction ? handleBeforeSend : undefined}
                onSent={() => {
                  onSendComplete?.();
                  handleClose();
                }}
                onEmailerClosed={() => {
                  onSendComplete?.();
                  handleClose();
                }}
              />
            )}
            {/* Step 1: Review & Edit Invoice Line Items - COMBINED */}
            {currentStep === 1 && (
              <div className="space-y-4 flex flex-col">
                {/* ============================================================
                    Phase 4 (Task 4.2) — Fixed Fee mode Step 1
                    Replace the full WIP table with a single pre-populated line.
                    Description is editable; amount is read-only.
                    ============================================================ */}
                {isFixedFeeMode && billingEvent ? (
                  <>
                    {/* Summary header */}
                    <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                      <div>
                        <h3 className="font-semibold text-lg">Fixed Fee Invoice Line</h3>
                        <p className="text-sm text-muted-foreground">
                          Edit the description if needed. The amount is fixed and cannot be changed here.
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-muted-foreground">Invoice Total</div>
                        <div className="text-2xl font-bold">{formatCurrency(billingEvent.amount)}</div>
                      </div>
                    </div>

                    {/* Single line item card */}
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/60 border-b">
                          <tr>
                            <th className="text-left px-4 py-3 font-medium text-muted-foreground">Description</th>
                            <th className="text-right px-4 py-3 font-medium text-muted-foreground w-40">Amount (excl. VAT)</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-t">
                            <td className="px-4 py-4">
                              <Textarea
                                value={fixedFeeDescription}
                                onChange={(e) => {
                                  const value = e.target.value.slice(0, 30);
                                  setFixedFeeDescription(value);
                                  // Keep invoiceLineItems in sync so Step 2 preview uses the edited description
                                  setInvoiceLineItems((prev) =>
                                    prev.map((item) =>
                                      item.id === `ff-line-${billingEvent.name}`
                                        ? { ...item, description: value }
                                        : item
                                    )
                                  );
                                }}
                                maxLength={30}
                                placeholder="Invoice line description…"
                                className="min-h-[60px] resize-none"
                              />
                              <div className="flex items-center justify-between mt-1.5">
                                <p className="text-xs text-muted-foreground">
                                  This description will appear on the invoice. You can edit it before continuing.
                                </p>
                                <p className={cn("text-xs", fixedFeeDescription.length >= 30 ? "text-destructive" : "text-muted-foreground")}>
                                  {fixedFeeDescription.length}/30
                                </p>
                              </div>
                            </td>
                            <td className="px-4 py-4 text-right">
                              <div className="inline-flex items-center gap-2">
                                <span className="text-lg font-semibold">
                                  {formatCurrency(billingEvent.amount)}
                                </span>
                                <Badge variant="secondary" className="text-xs">Fixed</Badge>
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">
                                Due: {billingEvent.dueDate
                                  ? new Date(billingEvent.dueDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
                                  : "—"}
                              </p>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* VAT note */}
                    <div className="flex items-start gap-3 p-4 bg-info-50 border border-info-200 rounded-lg text-sm">
                      <AlertCircle className="h-5 w-5 text-info-600 flex-shrink-0 mt-0.5" />
                      <div className="text-info-800">
                        <p className="font-medium">VAT will be calculated on the preview</p>
                        <p className="mt-0.5">
                          {customerVatRate}% VAT ({formatCurrency(billingEvent.amount * customerVatRate / 100)}) will be applied in Step 2.
                          Total due: {formatCurrency(billingEvent.amount * (1 + customerVatRate / 100))}.
                        </p>
                      </div>
                    </div>
                  </>
                ) : (
                <>
                {/* Header with totals and bulk actions */}
                <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                  <div>
                    <h3 className="font-semibold text-lg">Invoice Line Items</h3>
                    <p className="text-sm text-muted-foreground">
                      {invoiceLineItems.length} lines • Click any cell to edit
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-muted-foreground">Subtotal</div>
                    <div className="text-2xl font-bold">{formatCurrency(totals.subtotal)}</div>
                  </div>
                </div>

                {/* Billing Settings Applied Banner */}
                {((expenseRecharge === 'markup' && parseFloat(expenseMarkupPercent) > 0) ||
                  (purchaseRecharge === 'markup' && parseFloat(purchaseMarkupPercent) > 0) ||
                  expenseRecharge === 'not-rechargeable' ||
                  purchaseRecharge === 'not-rechargeable') && (
                  <div className="flex items-start gap-3 p-4 bg-info-50 border border-info-200 rounded-lg text-sm">
                    <AlertCircle className="h-5 w-5 text-info-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-medium text-info-900">Billing Settings Applied</p>
                      <div className="text-info-700 mt-1 space-y-1">
                        {expenseRecharge === 'markup' && parseFloat(expenseMarkupPercent) > 0 && (
                          <p>• Expenses marked up by {expenseMarkupPercent}%</p>
                        )}
                        {purchaseRecharge === 'markup' && parseFloat(purchaseMarkupPercent) > 0 && (
                          <p>• Purchases marked up by {purchaseMarkupPercent}%</p>
                        )}
                        {expenseRecharge === 'not-rechargeable' && (
                          <p>• Non-rechargeable expenses excluded</p>
                        )}
                        {purchaseRecharge === 'not-rechargeable' && (
                          <p>• Non-rechargeable purchases excluded</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Search and Bulk Actions Toolbar */}
                <div className="flex items-center gap-3 p-3 border rounded-lg bg-background">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search line items..."
                      value={lineSearchQuery}
                      onChange={(e) => setLineSearchQuery(e.target.value)}
                      className="pl-9"
                    />
                    {lineSearchQuery && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                        onClick={() => setLineSearchQuery("")}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>

                  {/* Filter Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsFilterSheetOpen(true)}
                  >
                    <Filter className="h-4 w-4 mr-2" />
                    Filters
                    {(filterDateFrom || filterDateTo || filterResourceFilter.length > 0 || filterCostCentreFilter.length > 0 || filterTypeFilter.length > 0) && (
                      <Badge variant="secondary" className="ml-2 h-5 px-1.5">
                        {[
                          filterDateFrom || filterDateTo ? 1 : 0,
                          filterResourceFilter.length > 0 ? 1 : 0,
                          filterCostCentreFilter.length > 0 ? 1 : 0,
                          filterTypeFilter.length > 0 ? 1 : 0,
                        ].reduce((a, b) => a + b, 0)}
                      </Badge>
                    )}
                  </Button>

                  {selectedLineIds.length > 0 && (
                    <>
                      <Badge variant="secondary" className="px-3 py-1">
                        {selectedLineIds.length} selected
                      </Badge>
                      
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm">
                            <Banknote className="h-4 w-4 mr-2" />
                            Bulk Adjustments
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                          <DropdownMenuLabel>Adjust Rates</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleBulkRateChange("increase", 10, true)}>
                            <Percent className="h-4 w-4 mr-2" />
                            Increase by 10%
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleBulkRateChange("increase", 20, true)}>
                            <Percent className="h-4 w-4 mr-2" />
                            Increase by 20%
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleBulkRateChange("decrease", 10, true)}>
                            <Percent className="h-4 w-4 mr-2" />
                            Decrease by 10%
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleBulkRateChange("increase", 10, false)}>
                            <Banknote className="h-4 w-4 mr-2" />
                            Increase by £10
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleBulkRateChange("increase", 25, false)}>
                            <Banknote className="h-4 w-4 mr-2" />
                            Increase by £25
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => setAgreedValueDialogOpen(true)}>
                            <TrendingDown className="h-4 w-4 mr-2" />
                            Set agreed value for selected lines
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleDeleteSelected}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Remove Selected
                      </Button>
                    </>
                  )}

                  {/* Edit-Draft mode: Add Lines button — opens unbilled WIP picker */}
                  {editDraft && (isAddLinesServerPaged || addLinesBadgeCount > 0) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setAddLinesSearch('');
                        setAddLinesSelected(new Map());
                        setAddLinesOpen(true);
                      }}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Lines
                      {addLinesBadgeCount > 0 && (
                        <Badge variant="secondary" className="ml-1.5 h-5 px-1.5">
                          {addLinesBadgeCount}
                        </Badge>
                      )}
                    </Button>
                  )}

                  {/* Configure Columns Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setColumnConfigOpen(true)}
                  >
                    <Settings2 className="h-4 w-4 mr-2" />
                    Configure
                  </Button>
                </div>

                {/* Editable Table - Full Height */}
                {(() => {
                  // Build column defs for DataTableEnhanced header + sort
                  // Column widths — must match the style widths on <td> cells in customRowRenderer below
                  const COL_WIDTHS: Record<string, number> = {
                    date: 100,
                    type: 90,
                    resource: 130,
                    description: 260,
                    costCentre: 110,
                    reference: 110,
                    originalValue: 110,
                    quantity: 90,
                    rate: 100,
                    amount: 110,
                  };

                  const lineColumnDefs: ColumnDef<InvoiceLineItem, unknown>[] = [
                    {
                      id: "select",
                      enableSorting: false,
                      meta: { width: 48 },
                      header: () => (
                        <Checkbox
                          checked={selectedLineIds.length === filteredAndSortedLines.length && filteredAndSortedLines.length > 0}
                          onCheckedChange={handleSelectAll}
                        />
                      ),
                      cell: () => null,
                    },
                    ...visibleColumns.map((col) => ({
                      id: col.id,
                      header: col.label,
                      meta: { width: COL_WIDTHS[col.id] ?? 120, align: ["originalValue", "quantity", "rate", "amount"].includes(col.id) ? "right" : "left" },
                      cell: () => null,
                    } as ColumnDef<InvoiceLineItem, unknown>)),
                    {
                      id: "actions",
                      enableSorting: false,
                      meta: { width: 48 },
                      header: "",
                      cell: () => null,
                    },
                  ];

                  return (
                    <DataTableEnhanced<InvoiceLineItem, unknown>
                      columns={lineColumnDefs}
                      data={paginatedLines}
                      columnSorts={columnSorts}
                      onColumnSortChange={(sorts) => { setColumnSorts(sorts); setCurrentPage(1); }}
                      emptyMessage={
                        <div className="text-center py-12 text-muted-foreground">
                          <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                          <p>No line items found</p>
                        </div>
                      }
                      customRowRenderer={(row) => {
                        const line = row.original;
                        return (
                          <TableRow
                            key={line.id}
                            className={cn(
                              "group",
                              selectedLineIds.includes(line.id) && "bg-muted/50 even:bg-muted/50 odd:bg-muted/50 hover:bg-muted/50",
                              line.isGrouped && !selectedLineIds.includes(line.id) && "bg-blue-50/30 even:bg-blue-50/30"
                            )}
                          >
                            {/* Checkbox — w-12 must match select column size:48 above */}
                            <TableCell className="w-12 shrink-0">
                              <Checkbox
                                checked={selectedLineIds.includes(line.id)}
                                onCheckedChange={() => handleSelectLine(line.id)}
                              />
                            </TableCell>

                            {/* Dynamic visible columns */}
                            {visibleColumns.map((column) => {
                              const w = COL_WIDTHS[column.id] ?? 120;
                              const tdStyle = { width: `${w}px`, minWidth: `${w}px`, maxWidth: `${w}px` };
                              switch (column.id) {
                                case "date":
                                  return (
                                    <TableCell key={column.id} style={tdStyle}>
                                      {format(line.date, "dd/MM/yyyy")}
                                    </TableCell>
                                  );
                                case "type":
                                  return (
                                    <TableCell key={column.id} style={tdStyle}>
                                      <Badge variant={line.type === 'Time' ? 'default' : line.type === 'Expense' ? 'secondary' : 'outline'}>
                                        {line.type}
                                      </Badge>
                                    </TableCell>
                                  );
                                case "resource":
                                  return (
                                    <TableCell key={column.id} style={tdStyle} className="truncate">
                                      {line.resource}
                                    </TableCell>
                                  );
                                case "description":
                                  return (
                                    <TableCell
                                      key={column.id}
                                      style={tdStyle}
                                      className={cn(
                                        "cursor-text hover:bg-muted/50 transition-colors",
                                        line.isGrouped && "bg-blue-50/50"
                                      )}
                                      onClick={() => editingCellId !== `${line.id}-description` && handleCellClick(line.id, "description", line.description)}
                                    >
                                      {editingCellId === `${line.id}-description` ? (
                                        <Textarea
                                          value={editingValue}
                                          onChange={(e) => setEditingValue(e.target.value)}
                                          onBlur={() => handleCellSave(line.id, "description")}
                                          onKeyDown={(e) => handleCellKeyDown(e, line.id, "description")}
                                          className="min-h-[60px] w-full"
                                          autoFocus
                                        />
                                      ) : (
                                        <div className="flex items-center gap-2 min-w-0">
                                          <span className="flex-1 text-sm truncate">{line.description}</span>
                                          <div className="flex items-center gap-1 shrink-0">
                                            {line.isEdited && !agreedValueLineIds.includes(line.id) && (
                                              <Badge variant="outline" className="text-xs">Edited</Badge>
                                            )}
                                            {agreedValueLineIds.includes(line.id) && (
                                              <Badge variant="outline" className="text-xs border-purple-300 text-purple-700 dark:border-purple-600 dark:text-purple-400 shrink-0">
                                                Agreed
                                              </Badge>
                                            )}
                                            <Edit2 className="h-3 w-3 opacity-0 group-hover:opacity-50 transition-opacity" />
                                          </div>
                                        </div>
                                      )}
                                    </TableCell>
                                  );
                                case "costCentre":
                                  return (
                                    <TableCell key={column.id} style={tdStyle} className="truncate">
                                      {line.costCentre}
                                    </TableCell>
                                  );
                                case "reference":
                                  return (
                                    <TableCell key={column.id} style={tdStyle} className="truncate">
                                      {line.reference}
                                    </TableCell>
                                  );
                                case "originalValue":
                                  return (
                                    <TableCell key={column.id} style={tdStyle} className="text-right text-muted-foreground">
                                      {formatCurrency(line.originalValue)}
                                    </TableCell>
                                  );
                                case "quantity":
                                  return (
                                    <TableCell
                                      key={column.id}
                                      style={tdStyle}
                                      className="text-right cursor-text hover:bg-muted/50 transition-colors"
                                      onClick={() => editingCellId !== `${line.id}-quantity` && handleCellClick(line.id, "quantity", line.quantity)}
                                    >
                                      {editingCellId === `${line.id}-quantity` ? (
                                        <Input
                                          type="number"
                                          step="0.01"
                                          value={editingValue}
                                          onChange={(e) => setEditingValue(e.target.value)}
                                          onBlur={() => handleCellSave(line.id, "quantity")}
                                          onKeyDown={(e) => handleCellKeyDown(e, line.id, "quantity")}
                                          onFocus={(e) => e.target.select()}
                                          className="text-right [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                          autoFocus
                                        />
                                      ) : (
                                        <div className="flex items-center justify-end gap-2">
                                          <span>{line.quantity.toFixed(2)}</span>
                                          <Edit2 className="h-3 w-3 opacity-0 group-hover:opacity-50 transition-opacity" />
                                        </div>
                                      )}
                                    </TableCell>
                                  );
                                case "rate":
                                  return (
                                    <TableCell
                                      key={column.id}
                                      style={tdStyle}
                                      className="text-right cursor-text hover:bg-muted/50 transition-colors"
                                      onClick={() => editingCellId !== `${line.id}-rate` && handleCellClick(line.id, "rate", line.rate)}
                                    >
                                      {editingCellId === `${line.id}-rate` ? (
                                        <Input
                                          type="number"
                                          step="0.01"
                                          value={editingValue}
                                          onChange={(e) => setEditingValue(e.target.value)}
                                          onBlur={() => handleCellSave(line.id, "rate")}
                                          onKeyDown={(e) => handleCellKeyDown(e, line.id, "rate")}
                                          onFocus={(e) => e.target.select()}
                                          className="text-right [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                          autoFocus
                                        />
                                      ) : (
                                        <div className="flex items-center justify-end gap-2">
                                          <span>{formatCurrency(line.rate)}</span>
                                          <Edit2 className="h-3 w-3 opacity-0 group-hover:opacity-50 transition-opacity" />
                                        </div>
                                      )}
                                    </TableCell>
                                  );
                                case "amount":
                                  return (
                                    <TableCell key={column.id} style={tdStyle} className="text-right font-medium">
                                      {formatCurrency(line.amount)}
                                    </TableCell>
                                  );
                                default:
                                  return <TableCell key={column.id} style={tdStyle} />;
                              }
                            })}

                            {/* Actions — w-12 (48px) must match actions column meta.width:48 above */}
                            <TableCell className="w-12" style={{ width: '48px', minWidth: '48px' }}>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => handleDeleteLine(line.id)}>
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Remove Line
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        );
                      }}
                      paginationContent={
                        <PaginationControls
                          pagination={pagination}
                          onPageChange={setCurrentPage}
                          onPageSizeChange={(size) => { setItemsPerPage(size); setCurrentPage(1); }}
                        />
                      }
                    />
                  );
                })()}
                {/* End of T&M path — close fragment + ternary opened for Fixed Fee mode (Phase 4) */}
                </>
                )}
              </div>
            )}



            {/* Step 2: Invoice Preview (Template-Driven) */}
            {currentStep === 2 && (
              <div className={`wizard-step2-grid grid h-full min-h-0 ${showInvoiceDetails ? 'grid-cols-[400px_1fr]' : 'grid-cols-1'}`}>
                {/* Left Panel - Invoice Details Editor */}
                {showInvoiceDetails && (
                  <div className="wizard-details-panel overflow-y-auto border-r p-6 min-h-0 space-y-4">
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm">Invoice Details</CardTitle>
                        <CardDescription className="text-xs">Edit details — changes update the preview in real time</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* Invoice Date */}
                        <div className="space-y-1.5">
                          <Label className="text-xs">Invoice Date</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className={cn(
                                  "w-full justify-start text-left font-normal text-xs",
                                  !invoiceDate && "text-muted-foreground"
                                )}
                              >
                                <CalendarIcon className="mr-2 h-3.5 w-3.5" />
                                {invoiceDate ? format(invoiceDate, "PPP") : "Pick a date"}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar
                                mode="single"
                                selected={invoiceDate}
                                onSelect={(date) => date && setInvoiceDate(date)}
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                        </div>

                        <Separator />

                        {/* ── Feature 9: Invoice Number (manual mode only) ──────────
                            Shown only when auto-numbering is OFF (controlled via
                            Settings > Project Settings > Billing).
                            TODO [ENGINEERING]: Remove the isAutoNumbering condition
                            and replace with a real-time API check. The field should
                            only appear when GET /api/company/billing-settings returns
                            { autoNumberingEnabled: false }.
                        */}
                        {!isAutoNumbering && (
                          <>
                            <div className="space-y-1.5">
                              <div className="flex items-center justify-between">
                                <Label className="text-xs" htmlFor="manualInvoiceNumber">
                                  Invoice Number
                                  <span className="text-destructive ml-0.5">*</span>
                                </Label>
                                {/* Last-used hint */}
                                <span className="text-[10px] text-muted-foreground font-mono">
                                  Last used: {billingInvoiceSettings.lastInvoiceNumber}
                                </span>
                              </div>
                              <Input
                                id="manualInvoiceNumber"
                                placeholder="e.g. INV-2025-005"
                                value={manualInvoiceNumber}
                                onChange={(e) => setManualInvoiceNumber(e.target.value)}
                                className={cn(
                                  "text-xs h-8 font-mono",
                                  isDuplicateInvoiceNumber && "border-destructive focus-visible:ring-destructive",
                                  isInvoiceNumberMissing && manualInvoiceNumber === '' && "border-muted-foreground/40"
                                )}
                                aria-describedby={isDuplicateInvoiceNumber ? "invoice-number-error" : undefined}
                              />
                              {/* Inline validation messages */}
                              {isDuplicateInvoiceNumber && (
                                <p id="invoice-number-error" className="text-[11px] text-destructive flex items-center gap-1 mt-1">
                                  <AlertCircle className="h-3 w-3 shrink-0" />
                                  This invoice number is already in use. Please enter a different number.
                                </p>
                              )}
                              {!isDuplicateInvoiceNumber && !isInvoiceNumberMissing && manualInvoiceNumber.trim().length > 0 && (
                                <p className="text-[11px] text-success-600 dark:text-success-400 flex items-center gap-1 mt-1">
                                  <CheckCircle className="h-3 w-3 shrink-0" />
                                  Number available
                                </p>
                              )}
                            </div>
                            <Separator />
                          </>
                        )}

                        {/* Customer PO Number — only shown when template includes PO Number field */}
                        {tplState.showPONumber && (
                          <>
                            <div className="space-y-1.5">
                              <Label className="text-xs" htmlFor="customerPo">Customer PO Number</Label>
                              <Input
                                id="customerPo"
                                placeholder="Enter PO number"
                                value={customerPoNumber}
                                onChange={(e) => setCustomerPoNumber(e.target.value)}
                                className="text-xs h-8"
                              />
                            </div>
                            <Separator />
                          </>
                        )}

                        {/* Project Period — only shown when template includes Billing Period field */}
                        {tplState.showBillingPeriod && (
                        <div className="space-y-1.5">
                          <Label className="text-xs">Period Covered</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className={cn(
                                  "w-full justify-start text-left font-normal text-xs",
                                  !projectPeriodFrom && !projectPeriodTo && "text-muted-foreground"
                                )}
                              >
                                <CalendarIcon className="mr-2 h-3.5 w-3.5" />
                                {projectPeriodFrom && projectPeriodTo
                                  ? `${format(projectPeriodFrom, "dd/MM/yy")} – ${format(projectPeriodTo, "dd/MM/yy")}`
                                  : projectPeriodFrom
                                    ? `${format(projectPeriodFrom, "dd/MM/yy")} – …`
                                    : "Select date range"}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar
                                mode="range"
                                selected={projectPeriodFrom ? { from: projectPeriodFrom, to: projectPeriodTo } : undefined}
                                onSelect={(range) => {
                                  setProjectPeriodFrom(range?.from);
                                  setProjectPeriodTo(range?.to);
                                }}
                                numberOfMonths={2}
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                        )}

                        {/* Invoice Notes — only shown when template has Notes enabled */}
                        {tplState.showNotes && (
                          <>
                            <Separator />
                            <div className="space-y-1.5">
                              <Label className="text-xs" htmlFor="invoiceNotes">Notes for this invoice</Label>
                              <Textarea
                                id="invoiceNotes"
                                placeholder="Add any notes specific to this invoice..."
                                value={invoiceNotes}
                                onChange={(e) => setInvoiceNotes(e.target.value)}
                                rows={3}
                                className="resize-none text-xs"
                              />
                              <p className="text-[10px] text-muted-foreground">These notes will appear alongside your template notes on the invoice.</p>
                            </div>
                          </>
                        )}

                      </CardContent>
                    </Card>

                    {/* Payment Info */}
                    <Card>
                      <CardContent className="pt-4 space-y-2">
                        <div className="flex justify-between text-xs">
                          <span className="text-muted-foreground">Payment Terms</span>
                          <span className="font-medium">{paymentTermsDescription}</span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-muted-foreground">Due Date</span>
                          <span className="font-medium">{format(calculateDueDate(invoiceDate, paymentTermsDueType, paymentTermsDays), "dd/MM/yyyy")}</span>
                        </div>
                      </CardContent>
                    </Card>

                    <div className="flex items-center gap-2 p-3 bg-success-50 border border-success-200 rounded-lg text-xs text-success-900">
                      <CheckCircle className="h-3.5 w-3.5 flex-shrink-0" />
                      <p>Ready to generate a draft invoice.</p>
                    </div>
                  </div>
                )}

                {/* Right Panel - Invoice Preview */}
                <div className="wizard-preview-canvas bg-muted/30 p-6 overflow-y-auto min-h-0">
                  {/* Preview Header Bar */}
                  <div className="wizard-preview-toolbar flex items-center justify-between mb-4">
                    <div className="flex items-center gap-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowInvoiceDetails(!showInvoiceDetails)}
                      >
                        {showInvoiceDetails ? <PanelLeftClose className="h-4 w-4 mr-1.5" /> : <PanelLeftOpen className="h-4 w-4 mr-1.5" />}
                        {showInvoiceDetails ? 'Hide Details' : 'Edit Details'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleExportPDF}
                        disabled={isExportingPdf}
                        className="text-success-600 hover:text-success-700 hover:bg-success-50"
                      >
                        <Download className="h-4 w-4 mr-1.5" />
                        {isExportingPdf ? 'Exporting…' : 'Export PDF'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.print()}
                      >
                        <Printer className="h-4 w-4 mr-1.5" />
                        Print
                      </Button>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <Select
                          value={template?.id?.toString() || ''}
                          onValueChange={(val) => {
                            const found = templates.find(t => t.id.toString() === val);
                            if (found) {
                              setTemplate(found);
                              // Task 2.12 — notify parent so the project's assigned template is updated
                              onTemplateChange?.(found.id);
                            }
                          }}
                        >
                          <SelectTrigger className="h-7 text-xs w-auto min-w-[180px] border-dashed">
                            <SelectValue placeholder="Select template" />
                          </SelectTrigger>
                          <SelectContent>
                            {templates.map((t) => (
                              <SelectItem key={t.id} value={t.id.toString()}>
                                {t.name}{t.isDefault ? ' (Default)' : ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <span className="text-xs text-muted-foreground">&bull; {finalInvoiceLines.length} items</span>
                      </div>
                      <Eye className="h-5 w-5 text-purple-600" />
                      <h3 className="text-base font-semibold">Invoice Preview</h3>
                    </div>
                  </div>

                  {/* Invoice Cap Warning */}
                  {exceedsCap && (
                    <div className="wizard-cap-warning flex items-start gap-3 p-4 bg-warning-50 border border-warning-200 rounded-lg mb-4">
                      <AlertCircle className="h-5 w-5 text-warning-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-semibold text-warning-900">Invoice Cap Exceeded</p>
                        <p className="text-sm text-warning-800 mt-1">
                          This invoice exceeds the project&apos;s &quot;Not to Exceed&quot; cap by{' '}
                          <span className="font-semibold">{formatCurrency(capExceededAmount)}</span>.
                          Cap: {formatCurrency(parseFloat(notToExceedCapAmount))} | Invoice: {formatCurrency(totals.subtotal * (1 + customerVatRate / 100))}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* A4 Paper Preview — Task 2.4: dimensions respect tplOrientation */}
                  <div className="wizard-paper-bg bg-slate-200 rounded-lg p-8 flex justify-center">
                    <div
                      className="wizard-paper bg-white p-8 rounded-lg shadow-sm border mx-auto origin-top"
                      style={tplPaperStyle}
                    >
                      <div className="space-y-6">
                        {tplSectionOrder.map((sectionId, idx) => {
                          const nextId = tplSectionOrder[idx + 1];
                          const isLastHeaderSection =
                            (sectionId === 'project' || sectionId === 'invoice' || sectionId === 'client') &&
                            nextId === 'lineItems';
                          // eslint-disable-next-line no-inner-declarations
                          function renderWizSection() { switch (sectionId) {
                            // ── LOGO ──
                            case 'logo': {
                              // Fallback: template logo → General Settings logo → hide entirely
                              const effectiveLogo = tplState.companyLogo || enrichedCompany?.logoUrl;
                              return tplState.showCompanyLogo && effectiveLogo ? (
                                <div key="logo" className={`flex ${tplState.logoPosition === 'left' ? 'justify-start' : tplState.logoPosition === 'center' ? 'justify-center' : 'justify-end'}`}>
                                  <img src={effectiveLogo} alt="Company Logo" className="h-20 w-auto max-w-[250px] object-contain" />
                                </div>
                              ) : null;
                            }

                            // ── COMPANY ──
                            case 'company':
                              return (tplState.showCompanyName || tplState.showCompanyAddress || tplState.showCompanyPhone || tplState.showCompanyVAT || tplState.showCompanyRegistration) ? (
                                <div key="company" className={`flex ${tplState.companyPosition === 'left' ? 'justify-start' : tplState.companyPosition === 'center' ? 'justify-center' : 'justify-end'}`}>
                                  <div className={`space-y-1 ${tplState.companyPosition === 'center' ? 'text-center' : tplState.companyPosition === 'right' ? 'text-right' : ''}`}>
                                    {tplState.showCompanyName && enrichedCompany && (
                                      <div className="font-semibold">{enrichedCompany.name}</div>
                                    )}
                                    {tplState.showCompanyAddress && enrichedCompany?.registeredAddress && (
                                      <div className="text-sm text-muted-foreground">
                                        {enrichedCompany.registeredAddress.line1}
                                        {enrichedCompany.registeredAddress.line2 && `, ${enrichedCompany.registeredAddress.line2}`}
                                        , {enrichedCompany.registeredAddress.city}
                                        {enrichedCompany.registeredAddress.county && `, ${enrichedCompany.registeredAddress.county}`}
                                        , {enrichedCompany.registeredAddress.postcode}
                                      </div>
                                    )}
                                    {tplState.showCompanyPhone && enrichedCompany?.phone && (
                                      <div className="text-sm text-muted-foreground">{enrichedCompany.phone}{enrichedCompany?.email ? ` | ${enrichedCompany.email}` : ''}</div>
                                    )}
                                    {tplState.showCompanyVAT && enrichedCompany?.vatNumber && (
                                      <div className="text-sm text-muted-foreground">VAT Reg: {enrichedCompany.vatNumber}</div>
                                    )}
                                    {tplState.showCompanyRegistration && enrichedCompany?.companyRegistrationNumber && (
                                      <div className="text-sm text-muted-foreground">Company Reg: {enrichedCompany.companyRegistrationNumber}</div>
                                    )}
                                  </div>
                                </div>
                              ) : null;

                            // ── CLIENT ──
                            case 'client':
                              return (tplState.showClientName || tplState.showClientAddress || tplState.showClientContact) ? (
                                <div key="client" className={`flex ${tplState.clientPosition === 'left' ? 'justify-start' : tplState.clientPosition === 'center' ? 'justify-center' : 'justify-end'}`}>
                                  <div className={`space-y-1 ${tplState.clientPosition === 'center' ? 'text-center' : tplState.clientPosition === 'right' ? 'text-right' : ''}`}>
                                    <div className="text-sm font-medium text-muted-foreground">Bill To:</div>
                                    {tplState.showClientName && enrichedClient && (
                                      <div className="font-medium">{enrichedClient.name}</div>
                                    )}
                                    {tplState.showClientAddress && enrichedClient?.invoiceAddress && (
                                      <div className="text-sm text-muted-foreground">
                                        {formatClientInvoiceAddress(enrichedClient.invoiceAddress)}
                                      </div>
                                    )}
                                    {tplState.showClientContact && enrichedClient?.vatNumber && (
                                      <div className="text-sm text-muted-foreground">VAT: {enrichedClient.vatNumber}</div>
                                    )}
                                    {!enrichedClient && (
                                      <div className="text-sm text-muted-foreground">No customer specified</div>
                                    )}
                                  </div>
                                </div>
                              ) : null;

                            // ── INVOICE DETAILS ──
                            case 'invoice':
                              return (tplState.showInvoiceNumber || tplState.showInvoiceDate || tplState.showDueDate) ? (
                                <div key="invoice" className={`flex ${tplState.invoiceDetailsPosition === 'left' ? 'justify-start' : tplState.invoiceDetailsPosition === 'center' ? 'justify-center' : 'justify-end'}`}>
                                  <div className={`space-y-1 ${tplState.invoiceDetailsPosition === 'center' ? 'text-center' : tplState.invoiceDetailsPosition === 'right' ? 'text-right' : ''}`}>
                                    {tplState.showInvoiceNumber && (
                                      <div className="text-2xl font-bold">INV-DRAFT</div>
                                    )}
                                    {tplState.showInvoiceDate && (
                                      <div className="text-sm text-muted-foreground">
                                        Date: {format(invoiceDate, "dd/MM/yyyy")}
                                      </div>
                                    )}
                                    {tplState.showDueDate && (
                                      <div className="text-sm text-muted-foreground">
                                        Due: {format(calculateDueDate(invoiceDate, paymentTermsDueType, paymentTermsDays), "dd/MM/yyyy")}
                                      </div>
                                    )}
                                    {customerPoNumber && (
                                      <div className="text-sm text-muted-foreground">
                                        PO: {customerPoNumber}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ) : null;

                            // ── PROJECT ──
                            case 'project':
                                              return (tplState.showProjectCode || tplState.showProjectName || tplState.showProjectReference || tplState.showProjectDescription || tplState.showProjectManager) ? (
                                                <div key="project" className={`flex ${tplState.projectPosition === 'left' ? 'justify-start' : tplState.projectPosition === 'center' ? 'justify-center' : 'justify-end'}`}>
                                                  <div className={`space-y-1 ${tplState.projectPosition === 'center' ? 'text-center' : tplState.projectPosition === 'right' ? 'text-right' : ''}`}>
                                                    {(tplState.showProjectCode || tplState.showProjectName) && (
                                                      <div>
                                                        <span className="text-sm text-muted-foreground">Project: </span>
                                                        <span className="font-medium">
                                                          {tplState.showProjectCode && tplState.showProjectName && `${projectCode || ''} - ${projectName}`}
                                                          {tplState.showProjectCode && !tplState.showProjectName && (projectCode || '')}
                                                          {!tplState.showProjectCode && tplState.showProjectName && projectName}
                                                        </span>
                                                      </div>
                                                    )}
                                                    {tplState.showProjectReference && (
                                                      <div className="text-sm text-muted-foreground">
                                                        {(projectPeriodFrom || projectPeriodTo) && (
                                                          <span>
                                                            Period: {projectPeriodFrom && format(projectPeriodFrom, "dd/MM/yyyy")}
                                                            {projectPeriodFrom && projectPeriodTo && ' - '}
                                                            {projectPeriodTo && format(projectPeriodTo, "dd/MM/yyyy")}
                                                          </span>
                                                        )}
                                                      </div>
                                                    )}
                                                    {tplState.showProjectDescription && projectDescription && (
                                                      <div>
                                                        <span className="text-sm text-muted-foreground">Description: </span>
                                                        <span>{projectDescription}</span>
                                                      </div>
                                                    )}
                                                    {tplState.showProjectManager && projectManagerName && (
                                                      <div>
                                                        <span className="text-sm text-muted-foreground">Project Manager: </span>
                                                        <span>{projectManagerName}</span>
                                                      </div>
                                                    )}
                                                  </div>
                                                </div>
                                              ) : null;

                            // ── LINE ITEMS TABLE ──
                            // Task 6.7d — delegated to WizLineItemsTable component (module level)
                            // to avoid Babel TSX parse errors in deeply nested switch-case JSX.
                            case 'lineItems':
                              return (
                                <WizLineItemsTable
                                  key="lineItems"
                                  tplState={tplState}
                                  tplColumnOrder={tplColumnOrder}
                                  finalInvoiceLines={finalInvoiceLines}
                                  groupDescriptionOverrides={groupDescriptionOverrides}
                                  setGroupDescriptionOverrides={setGroupDescriptionOverrides}
                                  vatRate={customerVatRate}
                                  vatCode={customerVatCode}
                                  rowColorMap={wizRowColorMap}
                                  groupRowColorMap={wizBsRowColorMap}
                                />
                              );

                            // ── TOTALS ──
                            case 'totals':
                              return (
                                <div key="totals" className="flex justify-end">
                                  <div className="w-64 space-y-2">
                                    {tplState.showSubtotal && (
                                      <div className="flex justify-between text-sm">
                                        <span className="text-muted-foreground">Subtotal:</span>
                                        <span>{formatCurrency(totals.subtotal)}</span>
                                      </div>
                                    )}
                                    {tplState.showTax && (
                                      <div className="flex justify-between text-sm">
                                        <span className="text-muted-foreground">VAT ({customerVatRate}%):</span>
                                        <span>{formatCurrency(totals.subtotal * (customerVatRate / 100))}</span>
                                      </div>
                                    )}
                                    {tplState.showDiscount && (
                                      <div className="flex justify-between text-sm">
                                        <span className="text-muted-foreground">Discount:</span>
                                        <span>-{formatCurrency(0)}</span>
                                      </div>
                                    )}
                                    {tplState.showTotal && (
                                      <div className="flex justify-between font-bold text-lg border-t pt-2" style={{ borderColor: tplState.primaryColor }}>
                                        <span style={{ color: tplState.primaryColor }}>Total:</span>
                                        <span>{formatCurrency(totals.subtotal * (1 + customerVatRate / 100))}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );

                            // ── FOOTER ──
                            case 'footer': {
                              if (!tplState.showBankDetails && !tplState.showNotes && !tplState.showTermsConditions) return null;
                              const tcDueDate = calculateDueDate(invoiceDate, paymentTermsDueType, paymentTermsDays);
                              const tcContext: TokenContext = {
                                client: {
                                  name: client?.name,
                                  paymentTermsDays,
                                  paymentTermsDescription,
                                  paymentTermsCode: paymentTermsDescription
                                    ? paymentTermsDescription.replace(/\s+/g, '').toUpperCase()
                                    : undefined,
                                },
                                invoice: {
                                  date: format(invoiceDate, 'dd/MM/yyyy'),
                                  dueDate: format(tcDueDate, 'dd/MM/yyyy'),
                                },
                                project: { name: projectName },
                                company: { name: enrichedCompany?.name },
                              };
                              const resolvedTc = tplState.termsContent
                                ? resolveInvoiceTokens(tplState.termsContent, tcContext)
                                : null;
                              return (
                                <div
                                  key="footer"
                                  className={`flex ${tplState.footerPosition === 'center' ? 'justify-center' : tplState.footerPosition === 'right' ? 'justify-end' : 'justify-start'} border-t pt-4`}
                                >
                                  <div className={`space-y-3 text-sm text-muted-foreground ${tplState.footerPosition === 'center' ? 'text-center' : tplState.footerPosition === 'right' ? 'text-right' : ''} w-full`}>
                                    {tplState.showBankDetails && (
                                      <div>
                                        <div className="font-medium text-foreground">Bank Details:</div>
                                        {enrichedCompany?.bankDetails ? (
                                          <div className="space-y-0.5">
                                            {enrichedCompany.bankDetails.bankName && <div>{enrichedCompany.bankDetails.bankName}</div>}
                                            {enrichedCompany.bankDetails.accountName && <div>Account Name: {enrichedCompany.bankDetails.accountName}</div>}
                                            {enrichedCompany.bankDetails.sortCode && <div>Sort Code: {enrichedCompany.bankDetails.sortCode}</div>}
                                            {enrichedCompany.bankDetails.accountNumber && <div>Account No: {enrichedCompany.bankDetails.accountNumber}</div>}
                                            {enrichedCompany.bankDetails.iban && <div>IBAN: {enrichedCompany.bankDetails.iban}</div>}
                                            {enrichedCompany.bankDetails.swiftBic && <div>SWIFT/BIC: {enrichedCompany.bankDetails.swiftBic}</div>}
                                          </div>
                                        ) : (
                                          <div className="text-muted-foreground/60 italic">No bank details set &mdash; add them in Settings &rarr; General</div>
                                        )}
                                      </div>
                                    )}
                                    {tplState.showNotes && (
                                      <div className="w-full">
                                        <div className="font-medium text-foreground mb-1">Notes:</div>
                                        <div
                                          className="border-2 border-dashed border-muted-foreground/40 rounded-md p-3 text-sm text-foreground min-h-[3em]"
                                          style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}
                                        >
                                          {(() => {
                                            const combined = [migrateNotes(tplState.notesContent), invoiceNotes].filter(Boolean).join('\n\n');
                                            return combined
                                              ? <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: combined }} />
                                              : <span className="text-muted-foreground/50">No notes</span>;
                                          })()}
                                        </div>
                                      </div>
                                    )}
                                    {tplState.showTermsConditions && (
                                      <div>
                                        <div className="font-medium text-foreground">Terms &amp; Conditions:</div>
                                        {resolvedTc ? (
                                          <div
                                            className="prose prose-sm max-w-none [&_.merge-chip]:inline-block [&_.merge-chip]:bg-primary/10 [&_.merge-chip]:text-primary [&_.merge-chip]:border [&_.merge-chip]:border-primary/30 [&_.merge-chip]:rounded [&_.merge-chip]:px-1 [&_.merge-chip]:text-xs [&_.merge-chip]:font-medium"
                                            dangerouslySetInnerHTML={{ __html: resolvedTc }}
                                          />
                                        ) : (
                                          <div className="italic text-muted-foreground/60">No terms and conditions entered.</div>
                                        )}
                                      </div>
                                    )}

                                  </div>
                                </div>
                              );
                            }

                            default:
                              return null;
                          } } // end renderWizSection / switch
                          const sectionContent = renderWizSection();
                          if (!sectionContent) return null;
                          return (
                            <React.Fragment key={sectionId}>
                              {sectionContent}
                              {isLastHeaderSection && (
                                <hr style={{ borderColor: tplState.primaryColor, borderTopWidth: 2, margin: 0 }} />
                              )}
                            </React.Fragment>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Task 5.4 — Backing Sheet second A4 page (pre-computed above return) */}
                  {wizBackingSheetNode}

                </div>
              </div>
            )}
          </div>
        </div>
      

      {/* Footer Bar - Sticky (hidden on Step 3 which has its own actions) */}
      {currentStep < 3 && (
      <div className="wizard-chrome border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button 
                variant="outline" 
                onClick={() => handleSaveAsDraft()}
                disabled={invoiceLineItems.length === 0 || isSavingDraft}
                className="text-info-600 hover:text-info-700 hover:bg-info-50"
              >
                <Save className="h-4 w-4 mr-2" />
                {isSavingDraft ? 'Saving…' : 'Save as Draft'}
              </Button>
              {editDraft && onReturnDraftToWip && (
                <Button
                  variant="outline"
                  onClick={() => setReturnToWipDialogOpen(true)}
                  disabled={isReturningToWip}
                  className="text-error-600 hover:text-error-700 hover:bg-error-50"
                >
                  <Undo2 className="h-4 w-4 mr-2" />
                  Return to WIP
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              {currentStep > 1 && currentStep < 3 && (
                <Button variant="outline" onClick={handleBack} size="sm">
                  <ChevronLeft className="h-4 w-4 mr-2" />
                  Back to Review
                </Button>
              )}
              {currentStep === 1 && (
                <Button onClick={handleNext} disabled={invoiceLineItems.length === 0} size="sm">
                  Continue to Preview
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              )}
              {currentStep === 2 && (
                <Button
                  onClick={() => setCurrentStep(3)}
                  disabled={
                    invoiceLineItems.length === 0 ||
                    isDuplicateInvoiceNumber ||
                    isInvoiceNumberMissing ||
                    isValidatingNumber
                  }
                  size="sm"
                  title={
                    isInvoiceNumberMissing
                      ? 'Enter an invoice number to continue'
                      : isDuplicateInvoiceNumber
                        ? 'This invoice number is already in use'
                        : isValidatingNumber
                          ? 'Checking invoice number...'
                          : undefined
                  }
                >
                  {isValidatingNumber ? 'Checking...' : 'Continue to Send'}
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
      )}

    </div>

    {/* Edit-Draft mode — Add Lines picker dialog */}
    <Dialog
      open={addLinesOpen}
      onOpenChange={(open) => {
        setAddLinesOpen(open);
        if (open) {
          // Refresh the current page so the picker reflects any WIP changes made
          // since the wizard was opened.
          void loadAddLinesPage(addLinesPage, addLinesPageSize);
        } else {
          setAddLinesSelected(new Map());
          setAddLinesSearch('');
        }
      }}
    >
      <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col gap-0 p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <DialogTitle>Add Lines from Unbilled Work</DialogTitle>
        </DialogHeader>

        {/* Search bar */}
        <div className="px-6 py-3 border-b shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={isAddLinesServerPaged ? 'Search all unbilled work…' : 'Search this page…'}
              value={addLinesSearch}
              onChange={(e) => setAddLinesSearch(e.target.value)}
              className="pl-9"
            />
            {addLinesSearch && (
              <Button
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                onClick={() => setAddLinesSearch('')}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Items list */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {(() => {
            if (addLinesLoading) {
              return (
                <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                  <Loader2 className="h-8 w-8 mb-3 animate-spin opacity-60" />
                  <p className="text-sm">Loading unbilled work…</p>
                </div>
              );
            }

            if (addLinesError) {
              return (
                <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                  <AlertCircle className="h-10 w-10 mb-3 opacity-40" />
                  <p className="text-sm mb-3">Couldn't load unbilled work.</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void loadAddLinesPage(addLinesPage, addLinesPageSize)}
                  >
                    Retry
                  </Button>
                </div>
              );
            }

            // Server-paged mode has already searched the whole feed, so only the
            // locally-added reinstatable rows still need filtering here.
            const filtered = isAddLinesServerPaged
              ? addLinesPageRows.filter(
                  (item) => !reinstatableRows.includes(item) || matchesPickerSearch(item, addLinesSearch)
                )
              : addLinesPageRows.filter((item) => matchesPickerSearch(item, addLinesSearch));

            if (filtered.length === 0) {
              return (
                <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                  <FileText className="h-10 w-10 mb-3 opacity-40" />
                  <p className="text-sm">
                    {addLinesSearch.trim()
                      ? 'No items match your search.'
                      : addLinesTotalCount === 0
                        ? 'All unbilled items are already on this draft.'
                        : 'All items on this page are already on this draft.'}
                  </p>
                </div>
              );
            }

            const allFilteredSelected = filtered.every((item) => addLinesSelected.has(item.id));
            const someFilteredSelected = filtered.some((item) => addLinesSelected.has(item.id));
            const reinstatableIds = new Set(reinstatableRows.map((item) => item.id));

            return (
              <table className="w-full text-sm">
                <thead className="bg-muted/60 sticky top-0 z-10">
                  <tr>
                    <th className="w-10 px-4 py-3">
                      <Checkbox
                        aria-label="Select all items on this page"
                        checked={allFilteredSelected && filtered.length > 0}
                        data-state={someFilteredSelected && !allFilteredSelected ? 'indeterminate' : undefined}
                        onCheckedChange={(checked) => {
                          setAddLinesSelected((prev) => setPageSelection(prev, filtered, checked === true));
                        }}
                      />
                    </th>
                    <th className="text-left px-3 py-3 font-medium text-muted-foreground">Date</th>
                    <th className="text-left px-3 py-3 font-medium text-muted-foreground">Type</th>
                    <th className="text-left px-3 py-3 font-medium text-muted-foreground">Resource</th>
                    <th className="text-left px-3 py-3 font-medium text-muted-foreground">Description</th>
                    <th className="text-right px-4 py-3 font-medium text-muted-foreground">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((item) => {
                    const isSelected = addLinesSelected.has(item.id);
                    return (
                      <tr
                        key={item.id}
                        className={cn(
                          'border-t cursor-pointer hover:bg-muted/40 transition-colors',
                          isSelected && 'bg-primary/5 hover:bg-primary/10'
                        )}
                        onClick={() => {
                          setAddLinesSelected((prev) => toggleRowSelection(prev, item));
                        }}
                      >
                        <td className="w-10 px-4 py-3">
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => {
                              setAddLinesSelected((prev) => toggleRowSelection(prev, item));
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                        <td className="px-3 py-3 text-muted-foreground whitespace-nowrap">
                          {item.date ? new Date(item.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                        </td>
                        <td className="px-3 py-3">
                          <Badge variant="secondary" className="text-xs font-normal">
                            {item.type || 'Time'}
                          </Badge>
                          {reinstatableIds.has(item.id) && (
                            <Badge variant="outline" className="ml-1.5 text-xs font-normal">
                              Removed from draft
                            </Badge>
                          )}
                        </td>
                        <td className="px-3 py-3 text-muted-foreground max-w-[130px] truncate">
                          {item.resource || '—'}
                        </td>
                        <td className="px-3 py-3 max-w-[240px]">
                          <span className="line-clamp-2">{item.description}</span>
                        </td>
                        <td className="px-4 py-3 text-right font-medium tabular-nums whitespace-nowrap">
                          {new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(item.amount)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            );
          })()}
        </div>

        {/* Paging — independent of the Unbilled Work table's paging */}
        {addLinesTotalCount > 0 && (
          <div className="px-6 py-3 border-t shrink-0">
            <PaginationControls
              pagination={calculatePagination(addLinesTotalCount, addLinesPage, addLinesPageSize)}
              onPageChange={handleAddLinesPageChange}
              onPageSizeChange={handleAddLinesPageSizeChange}
              layout="summary"
              itemLabel="unbilled items"
            />
            {reinstatableRows.length > 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                Plus {reinstatableRows.length} line{reinstatableRows.length !== 1 ? 's' : ''} removed from this draft, shown on page 1.
              </p>
            )}
          </div>
        )}

        <DialogFooter className="px-6 py-4 border-t shrink-0 flex items-center justify-between sm:justify-between gap-3">
          <div className="text-sm text-muted-foreground">
            {addLinesSelected.size > 0
              ? `${addLinesSelected.size} item${addLinesSelected.size !== 1 ? 's' : ''} selected`
              : 'Select items to add'}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setAddLinesOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmAddLines}
              disabled={addLinesSelected.size === 0}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add {addLinesSelected.size > 0 ? `${addLinesSelected.size} ` : ''}Line{addLinesSelected.size !== 1 ? 's' : ''}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Edit-Draft mode — Return to WIP confirmation */}
    <AlertDialog open={returnToWipDialogOpen} onOpenChange={setReturnToWipDialogOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Return to WIP?</AlertDialogTitle>
          <AlertDialogDescription>
            This will delete the draft invoice and return all associated WIP items back to Unbilled Work. This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isReturningToWip}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); handleReturnToWip(); }}
            disabled={isReturningToWip}
            className="bg-error-600 hover:bg-error-700 focus:ring-error-600"
          >
            {isReturningToWip ? 'Returning…' : 'Return to WIP'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* Advanced Filters Sheet */}
    <Sheet open={isFilterSheetOpen} onOpenChange={setIsFilterSheetOpen}>
      <SheetContent className="w-[400px] sm:w-[540px] flex flex-col">
        <SheetHeader className="px-6 pt-6">
          <SheetTitle>Advanced Filters</SheetTitle>
          <SheetDescription>
            Apply advanced filters to your invoice line items
          </SheetDescription>
        </SheetHeader>
        
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          {/* Date Range Filter */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Date Range</Label>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">From Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {draftDateFrom ? (
                        draftDateFrom.toLocaleDateString()
                      ) : (
                        <span className="text-muted-foreground">Pick a date</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={draftDateFrom}
                      onSelect={setDraftDateFrom}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                {draftDateFrom && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full"
                    onClick={() => setDraftDateFrom(undefined)}
                  >
                    Clear
                  </Button>
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">To Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {draftDateTo ? (
                        draftDateTo.toLocaleDateString()
                      ) : (
                        <span className="text-muted-foreground">Pick a date</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={draftDateTo}
                      onSelect={setDraftDateTo}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                {draftDateTo && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full"
                    onClick={() => setDraftDateTo(undefined)}
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>
          </div>

          <Separator />

          {/* Resource Filter */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Resources</Label>
            <MultiSelect
              options={Array.from(new Set(invoiceLineItems.map(item => item.resource))).map(resource => ({
                value: resource,
                label: resource
              }))}
              placeholder="Select resources..."
              emptyMessage="No resources found."
              value={draftResourceFilter}
              onValueChange={setDraftResourceFilter}
            />
          </div>

          <Separator />

          {/* Cost Centre Filter */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Cost Centres</Label>
            <MultiSelect
              options={Array.from(new Set(invoiceLineItems.map(item => item.costCentre).filter(cc => cc !== '—'))).map(costCentre => ({
                value: costCentre,
                label: costCentre
              }))}
              placeholder="Select cost centres..."
              emptyMessage="No cost centres found."
              value={draftCostCentreFilter}
              onValueChange={setDraftCostCentreFilter}
            />
          </div>

          <Separator />

          {/* Types Filter */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Types</Label>
            <MultiSelect
              options={[
                { value: "Time", label: "Time" },
                { value: "Expense", label: "Expense" },
                { value: "Purchase", label: "Purchase" },
              ]}
              placeholder="Select types..."
              emptyMessage="No types found."
              value={draftTypeFilter}
              onValueChange={setDraftTypeFilter}
            />
          </div>
        </div>

        <div className="flex gap-3 px-6 py-4 border-t mt-auto">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => {
              setDraftDateFrom(undefined);
              setDraftDateTo(undefined);
              setDraftResourceFilter([]);
              setDraftCostCentreFilter([]);
              setDraftTypeFilter([]);
              setFilterDateFrom(undefined);
              setFilterDateTo(undefined);
              setFilterResourceFilter([]);
              setFilterCostCentreFilter([]);
              setFilterTypeFilter([]);
            }}
          >
            Clear All
          </Button>
          <Button
            className="flex-1"
            onClick={() => {
              setFilterDateFrom(draftDateFrom);
              setFilterDateTo(draftDateTo);
              setFilterResourceFilter(draftResourceFilter);
              setFilterCostCentreFilter(draftCostCentreFilter);
              setFilterTypeFilter(draftTypeFilter);
              setIsFilterSheetOpen(false);
            }}
          >
            Apply Filters
          </Button>
        </div>
      </SheetContent>
    </Sheet>

    {/* Column Configurator Drawer */}
    <ColumnConfiguratorDrawer
      open={columnConfigOpen}
      onOpenChange={setColumnConfigOpen}
      columns={columns}
      onSave={setColumns}
      defaultColumns={INVOICE_LINE_DEFAULT_COLUMNS}
    />

    {/* Feature 5 — Agreed Value Dialog */}
    <AgreedValueDialog
      open={agreedValueDialogOpen}
      onOpenChange={setAgreedValueDialogOpen}
      selectedLines={
        invoiceLineItems
          .filter((item) => selectedLineIds.includes(item.id))
          .map((item): AgreedValueLineItem => ({
            id: item.id,
            description: item.description,
            amount: item.amount,
          }))
      }
      onConfirm={handleAgreedValueConfirm}
    />
    </>
    </TooltipProvider>
  );
}
